import { C as useAssetStore, S as AssetStoreProvider, _ as replaceSelectedImage, b as ToolbarSelect, c as mergeMenus, d as PageBackgroundFields, g as isProbablyImage, h as insertImageFiles, i as OutlinePane, m as Icon, n as Ruler, p as ImageControls, s as group, t as ShortcutSheet, u as MenuBar, v as ToolbarButton, x as ToolbarSeparator, y as ToolbarColor } from "./ShortcutSheet-D-ElFRSm.mjs";
import { n as printDocument, t as applyPrintGeometry } from "./printDocument-F_WrOoo7.mjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EditorContent, NodeViewWrapper, ReactNodeViewRenderer, useEditor, useEditorState } from "@tiptap/react";
import { CELL_PROPERTY_ATTRIBUTES, CELL_VERTICAL_ALIGNS, DEFAULT_BLOCK_STYLE, DEFAULT_HIGHLIGHT_COLOR, DEFAULT_PAGE_SETUP, HEADING_LEVELS, IMAGE_LAYOUTS, INDENT_STEP_PT, LINE_HEIGHT_PRESETS, MARK_TYPES, MAX_BORDER_WIDTH_PX, MAX_INDENT_PT, MAX_PADDING_PX, NODE_TYPES, NO_PAGE_BACKGROUND, TEXT_ALIGNMENTS, canUploadFonts, cellStyle, countText, createEmptyContent, createPageGeometry, cssPxToPt, findMatches, isImageLayout, isPrinted, pageBackgroundCss, paginate, parseAssetUri, ptToCssPx, readCellProperties, toHexColor, verticalOffsetPt } from "@burfy/docs-core";
import StarterKit from "@tiptap/starter-kit";
import { Placeholder } from "@tiptap/extensions";
import { TaskItem, TaskList } from "@tiptap/extension-list";
import { Table, TableCell, TableHeader, TableRow } from "@tiptap/extension-table";
import { BackgroundColor, Color, FontFamily, FontSize, TextStyle } from "@tiptap/extension-text-style";
import Highlight from "@tiptap/extension-highlight";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import TextAlign from "@tiptap/extension-text-align";
import { Extension, Mark, Node as Node$1, mergeAttributes } from "@tiptap/core";
import { Plugin, PluginKey, TextSelection } from "@tiptap/pm/state";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import DOMPurify from "dompurify";
import { DEFAULT_TIMEOUT_MS, LruCache, candidateKey, findRomanRun, getLanguage, learningKey, listLanguages, moveHighlight, planInsertion, rankCandidates, recordChoice, resolveImeKey, selectSpeechEngine, speechLocale, transliterate } from "@burfy/input-tools";
import { CellSelection } from "@tiptap/pm/tables";
import { canSplit } from "@tiptap/pm/transform";
import { z } from "zod";
//#region src/extensions/table-cell-properties.ts
/** The parsed properties of a cell element, from its own style attribute. */
function fromElement(element) {
	const style = element.style;
	const borderStyle = style.borderTopStyle || style.borderStyle;
	const border = style.borderTopWidth || style.borderWidth;
	/**
	* `border: none` reports a *width* of `medium`, not of zero.
	*
	* Reading the width alone therefore turned a deliberately borderless cell
	* back into a bordered one on every reload. The style is what carries the
	* intent, so it is read first.
	*/
	const borderWidthPx = borderStyle === "none" || borderStyle === "hidden" ? 0 : border ? Number.parseFloat(border) : null;
	return readCellProperties({
		backgroundColor: style.backgroundColor || element.getAttribute("bgcolor"),
		verticalAlign: style.verticalAlign || element.getAttribute("valign"),
		borderColor: style.borderTopColor || style.borderColor,
		borderWidthPx,
		paddingPx: style.padding ? Number.parseFloat(style.padding) : null
	});
}
/**
* One attribute per property, all rendering into a single `style`.
*
* TipTap merges the objects each `renderHTML` returns, so every property has
* to produce the whole declaration list rather than its own fragment —
* otherwise the last one to render wins and the rest vanish.
*/
function cellAttributes() {
	const spec = (name) => ({
		default: null,
		parseHTML: (element) => fromElement(element)[name],
		renderHTML: (attributes) => {
			const style = cellStyle(readCellProperties(attributes));
			return style ? { style } : {};
		}
	});
	return {
		backgroundColor: spec("backgroundColor"),
		verticalAlign: spec("verticalAlign"),
		borderColor: spec("borderColor"),
		borderWidthPx: spec("borderWidthPx"),
		paddingPx: spec("paddingPx")
	};
}
const TableCellWithProperties = TableCell.extend({ addAttributes() {
	return {
		...this.parent?.(),
		...cellAttributes()
	};
} });
const TableHeaderWithProperties = TableHeader.extend({ addAttributes() {
	return {
		...this.parent?.(),
		...cellAttributes()
	};
} });
//#endregion
//#region src/extensions/paragraph-style.ts
/** Parse a CSS number, rejecting anything that is not finite. */
function parseNumeric(value) {
	if (!value) return null;
	const parsed = Number.parseFloat(value);
	return Number.isFinite(parsed) ? parsed : null;
}
function renderPt(property, value) {
	return typeof value === "number" ? { style: `${property}: ${value}pt` } : {};
}
const ParagraphStyle = Extension.create({
	name: "paragraphStyle",
	addOptions() {
		return {
			types: [NODE_TYPES.paragraph, NODE_TYPES.heading],
			indentStepPt: INDENT_STEP_PT,
			maxIndentPt: MAX_INDENT_PT
		};
	},
	addGlobalAttributes() {
		return [{
			types: this.options.types,
			attributes: {
				lineHeight: {
					default: null,
					parseHTML: (element) => parseNumeric(element.style.lineHeight),
					renderHTML: (attributes) => typeof attributes["lineHeight"] === "number" ? { style: `line-height: ${attributes["lineHeight"]}` } : {}
				},
				spaceBeforePt: {
					default: null,
					parseHTML: (element) => parseNumeric(element.style.marginTop),
					renderHTML: (attributes) => renderPt("margin-top", attributes["spaceBeforePt"])
				},
				spaceAfterPt: {
					default: null,
					parseHTML: (element) => parseNumeric(element.style.marginBottom),
					renderHTML: (attributes) => renderPt("margin-bottom", attributes["spaceAfterPt"])
				},
				indentPt: {
					default: null,
					parseHTML: (element) => parseNumeric(element.style.marginLeft),
					renderHTML: (attributes) => renderPt("margin-left", attributes["indentPt"])
				},
				indentRightPt: {
					default: null,
					parseHTML: (element) => parseNumeric(element.style.marginRight),
					renderHTML: (attributes) => renderPt("margin-right", attributes["indentRightPt"])
				},
				firstLineIndentPt: {
					default: null,
					parseHTML: (element) => parseNumeric(element.style.textIndent),
					renderHTML: (attributes) => renderPt("text-indent", attributes["firstLineIndentPt"])
				}
			}
		}];
	},
	addCommands() {
		return {
			setLineHeight: (lineHeight) => ({ commands }) => this.options.types.every((type) => commands.updateAttributes(type, { lineHeight })),
			setSpaceBefore: (pt) => ({ commands }) => this.options.types.every((type) => commands.updateAttributes(type, { spaceBeforePt: pt })),
			setSpaceAfter: (pt) => ({ commands }) => this.options.types.every((type) => commands.updateAttributes(type, { spaceAfterPt: pt })),
			/**
			* Set any combination of the three indents at once.
			*
			* One command rather than three: dragging a ruler marker often moves two
			* of them together — the left marker carries the first line with it —
			* and separate commands would put two entries in the undo history for
			* one gesture.
			*/
			setIndents: (indents) => ({ commands }) => this.options.types.every((type) => commands.updateAttributes(type, {
				...indents.leftPt === void 0 ? {} : { indentPt: indents.leftPt },
				...indents.rightPt === void 0 ? {} : { indentRightPt: indents.rightPt },
				...indents.firstLinePt === void 0 ? {} : { firstLineIndentPt: indents.firstLinePt }
			})),
			indent: () => ({ editor, commands }) => this.options.types.every((type) => {
				const current = editor.getAttributes(type)["indentPt"];
				const next = Math.min((typeof current === "number" ? current : 0) + this.options.indentStepPt, this.options.maxIndentPt);
				return commands.updateAttributes(type, { indentPt: next });
			}),
			outdent: () => ({ editor, commands }) => this.options.types.every((type) => {
				const current = editor.getAttributes(type)["indentPt"];
				const next = Math.max((typeof current === "number" ? current : 0) - this.options.indentStepPt, 0);
				return commands.updateAttributes(type, { indentPt: next === 0 ? null : next });
			})
		};
	},
	addKeyboardShortcuts() {
		return {
			"Mod-]": () => this.editor.commands.indent(),
			"Mod-[": () => this.editor.commands.outdent()
		};
	}
});
//#endregion
//#region src/extensions/page-break.ts
const PageBreak = Node$1.create({
	name: NODE_TYPES.pageBreak,
	group: "block",
	atom: true,
	selectable: true,
	draggable: false,
	parseHTML() {
		return [{ tag: "div[data-page-break]" }];
	},
	renderHTML() {
		return ["div", {
			"data-page-break": "true",
			class: "burfy-docs-explicit-break",
			contenteditable: "false"
		}];
	},
	addCommands() {
		return { insertPageBreak: () => ({ chain }) => chain().insertContent({ type: this.name }).createParagraphNear().focus().run() };
	},
	addKeyboardShortcuts() {
		return { "Mod-Enter": () => this.editor.commands.insertPageBreak() };
	}
});
//#endregion
//#region src/ime/translit-source.ts
/**
* Remembers the roman text a transliterated word came from.
*
* This is what lets someone put the caret back on a converted word and press
* Ctrl/Cmd+G to see the candidates again — no re-typing, and no guessing at
* what they originally meant. Nobody else's transliteration does this, and it
* costs one invisible mark.
*
* It is editorial metadata, not formatting: exporters drop it, and it is
* cleared as soon as the text it describes is edited, because at that point
* the recorded roman source is no longer what produced these characters.
*/
/** Set on transactions the IME itself makes, so they are not self-clearing. */
const IME_COMMIT_META = "burfyImeCommit";
const clearOnEditKey = new PluginKey("burfyTranslitClearOnEdit");
const TranslitSource = Mark.create({
	name: MARK_TYPES.translitSource,
	inclusive: false,
	addAttributes() {
		return {
			roman: {
				default: null,
				parseHTML: (element) => element.getAttribute("data-translit"),
				renderHTML: (attributes) => attributes["roman"] ? { "data-translit": attributes["roman"] } : {}
			},
			lang: {
				default: null,
				parseHTML: (element) => element.getAttribute("data-translit-lang"),
				renderHTML: (attributes) => attributes["lang"] ? { "data-translit-lang": attributes["lang"] } : {}
			}
		};
	},
	parseHTML() {
		return [{ tag: "span[data-translit]" }];
	},
	renderHTML({ HTMLAttributes }) {
		return [
			"span",
			mergeAttributes(HTMLAttributes),
			0
		];
	},
	addProseMirrorPlugins() {
		const markName = this.name;
		return [new Plugin({
			key: clearOnEditKey,
			/**
			* Drop the mark from any text the user edits.
			*
			* Once a character inside a converted word changes, the recorded roman
			* source no longer describes what is there — re-opening candidates
			* from it would offer replacements for a word that no longer exists.
			*/
			appendTransaction(transactions, _oldState, newState) {
				const edits = transactions.filter((transaction) => transaction.docChanged && !transaction.getMeta("burfyImeCommit"));
				if (edits.length === 0) return null;
				const markType = newState.schema.marks[markName];
				if (!markType) return null;
				const ranges = [];
				for (const transaction of edits) for (const stepMap of transaction.mapping.maps) stepMap.forEach((_oldStart, _oldEnd, newStart, newEnd) => {
					ranges.push({
						from: newStart,
						to: newEnd
					});
				});
				const size = newState.doc.content.size;
				const clamp = (value) => Math.max(0, Math.min(value, size));
				const tr = newState.tr;
				let changed = false;
				for (const range of ranges) {
					const from = clamp(range.from);
					const to = clamp(range.to);
					const $pos = newState.doc.resolve(from);
					if ($pos.depth === 0) continue;
					newState.doc.nodesBetween($pos.start(), $pos.end(), (node, pos) => {
						if (!node.isText) return;
						if (!node.marks.some((mark) => mark.type === markType)) return;
						const nodeFrom = pos;
						const nodeTo = pos + node.nodeSize;
						if (nodeTo < from || nodeFrom > to) return;
						tr.removeMark(nodeFrom, nodeTo, markType);
						changed = true;
					});
				}
				return changed ? tr.setMeta("addToHistory", false) : null;
			}
		})];
	}
});
//#endregion
//#region src/images/ImageView.tsx
/**
* Renders one image.
*
* Two jobs: turn the document's `asset:<id>` reference into something an
* `<img>` can load, and offer a drag handle to resize it.
*
* The resolved URL is released on unmount. An editor that minted an object URL
* per render and never revoked them would hold every image in the document in
* memory for the life of the session, whether or not it was still on screen.
*/
function ImageView({ node, selected, updateAttributes, editor }) {
	const store = useAssetStore();
	const [resolved, setResolved] = useState(null);
	const [failed, setFailed] = useState(false);
	const reference = typeof node.attrs["src"] === "string" ? node.attrs["src"] : "";
	const layout = isImageLayout(node.attrs["layout"]) ? node.attrs["layout"] : "inline";
	const widthPx = typeof node.attrs["widthPx"] === "number" ? node.attrs["widthPx"] : null;
	const alt = typeof node.attrs["alt"] === "string" ? node.attrs["alt"] : "";
	const assetId = parseAssetUri(reference);
	/**
	* A plain URL is usable as-is; only our own `asset:` references have to be
	* resolved. Derived during render rather than assigned from an effect —
	* setting state in an effect body just to copy a prop causes a second render
	* for no reason.
	*/
	const src = assetId ? resolved : reference || null;
	useEffect(() => {
		if (!assetId || !store) return;
		let cancelled = false;
		store.resolveUrl(assetId).then((url) => {
			if (cancelled) return;
			setResolved(url);
			setFailed(url === null);
		}).catch(() => {
			if (!cancelled) setFailed(true);
		});
		return () => {
			cancelled = true;
			store.release(assetId);
		};
	}, [assetId, store]);
	const dragState = useRef(null);
	/**
	* Listeners are torn down through an `AbortController` rather than matching
	* `removeEventListener` calls: the handler that ends a drag would otherwise
	* have to reference itself, and the identity passed to `remove` need not be
	* the one that was added.
	*/
	const dragAbort = useRef(null);
	const startDrag = useCallback((event) => {
		event.preventDefault();
		const image = event.currentTarget.parentElement?.querySelector("img");
		const startWidth = widthPx ?? image?.offsetWidth ?? 0;
		/**
		* Measured, not passed in.
		*
		* The page is drawn inside a CSS transform, and the ratio of what is on
		* screen to what the layout says *is* that transform's scale. Reading it
		* off the element needs no prop threaded down through the node view, and
		* cannot fall out of step with the value actually rendered.
		*/
		const rendered = image?.getBoundingClientRect().width ?? 0;
		const layout = image?.offsetWidth ?? 0;
		const scale = layout > 0 && rendered > 0 ? rendered / layout : 1;
		dragState.current = {
			startX: event.clientX,
			startWidth,
			scale
		};
		dragAbort.current?.abort();
		const controller = new AbortController();
		dragAbort.current = controller;
		window.addEventListener("pointermove", (move) => {
			const drag = dragState.current;
			if (!drag) return;
			const delta = (move.clientX - drag.startX) / drag.scale;
			const next = Math.max(24, drag.startWidth + delta);
			updateAttributes({ widthPx: Math.round(next) });
		}, { signal: controller.signal });
		window.addEventListener("pointerup", () => {
			dragState.current = null;
			controller.abort();
			dragAbort.current = null;
		}, { signal: controller.signal });
	}, [widthPx, updateAttributes]);
	useEffect(() => () => dragAbort.current?.abort(), []);
	return /* @__PURE__ */ jsxs(NodeViewWrapper, {
		as: "span",
		className: "burfy-docs-image-wrapper",
		"data-layout": layout,
		"data-selected": selected || void 0,
		children: [src && !failed ? /* @__PURE__ */ jsx("img", {
			className: "burfy-docs-image",
			src,
			alt,
			draggable: false,
			style: widthPx ? { width: `${widthPx}px` } : void 0,
			onError: () => setFailed(true)
		}) : /* @__PURE__ */ jsx("span", {
			className: "burfy-docs-image-missing",
			role: "img",
			"aria-label": alt || "Image",
			children: failed ? "Image unavailable" : "Loading image…"
		}), selected && editor.isEditable && /* @__PURE__ */ jsx("span", {
			className: "burfy-docs-image-handle",
			role: "separator",
			"aria-label": "Resize image",
			onPointerDown: startDrag
		})]
	});
}
/**
* Update a selected image's attributes without losing the selection.
*
* Re-rendering the node view drops the `NodeSelection`, which takes the image
* controls off the toolbar. Every command that touches an image's attributes
* needs this, so it is written once: the version where each command remembers
* for itself is the version where `setImageLayout` was fixed and
* `setImageWidth` — eight lines below it — was not, so dragging the resize
* handle deselected the picture on the first pointer move and the Reset button
* vanished exactly when someone would reach for it.
*/
function updateSelectedImage({ editor, chain }, name, attributes) {
	const { from } = editor.state.selection;
	const wasSelected = editor.isActive(name);
	return chain().updateAttributes(name, attributes).command(({ commands, tr }) => {
		if (!wasSelected || tr.doc.nodeAt(from)?.type.name !== name) return true;
		return commands.setNodeSelection(from);
	}).run();
}
const ImageNode = Node$1.create({
	name: NODE_TYPES.image,
	group: "inline",
	inline: true,
	atom: true,
	draggable: true,
	addAttributes() {
		return {
			src: { default: null },
			alt: { default: null },
			/** Rendered width. Height follows from the intrinsic aspect ratio. */
			widthPx: {
				default: null,
				parseHTML: (element) => {
					const value = Number.parseFloat(element.getAttribute("width") ?? "");
					return Number.isFinite(value) ? value : null;
				},
				renderHTML: (attributes) => typeof attributes["widthPx"] === "number" ? { width: String(Math.round(attributes["widthPx"])) } : {}
			},
			heightPx: {
				default: null,
				parseHTML: (element) => {
					const value = Number.parseFloat(element.getAttribute("height") ?? "");
					return Number.isFinite(value) ? value : null;
				},
				renderHTML: (attributes) => typeof attributes["heightPx"] === "number" ? { height: String(Math.round(attributes["heightPx"])) } : {}
			},
			layout: {
				default: "inline",
				parseHTML: (element) => {
					const value = element.getAttribute("data-layout");
					return isImageLayout(value) ? value : "inline";
				},
				renderHTML: (attributes) => ({ "data-layout": isImageLayout(attributes["layout"]) ? attributes["layout"] : "inline" })
			}
		};
	},
	parseHTML() {
		return [{ tag: "img[src]" }];
	},
	renderHTML({ HTMLAttributes }) {
		return ["img", mergeAttributes(HTMLAttributes, {
			class: "burfy-docs-image",
			alt: HTMLAttributes["alt"] ?? ""
		})];
	},
	addNodeView() {
		return ReactNodeViewRenderer(ImageView);
	},
	addCommands() {
		return {
			insertImage: (attributes) => ({ commands }) => commands.insertContent({
				type: this.name,
				attrs: {
					layout: "inline",
					...attributes
				}
			}),
			setImageLayout: (layout) => (props) => IMAGE_LAYOUTS.includes(layout) ? updateSelectedImage(props, this.name, { layout }) : false,
			/**
			* Undo a manual resize.
			*
			* The natural size is read from the rendered `<img>` rather than kept on
			* the node: the browser already knows it, and a second copy of the same
			* number is a second thing that can be wrong. The column width comes
			* from the editor's own element, which is the measurement import uses —
			* so resetting lands exactly where inserting would have.
			*/
			resetImageSize: () => ({ editor, commands }) => {
				const selected = editor.view.dom.querySelector(".ProseMirror-selectednode img.burfy-docs-image, img.burfy-docs-image.ProseMirror-selectednode");
				const naturalWidth = selected?.naturalWidth ?? 0;
				const naturalHeight = selected?.naturalHeight ?? 0;
				if (naturalWidth <= 0 || naturalHeight <= 0)
 /**
				* Nothing measurable — an asset that failed to resolve, one still
				* decoding, an SVG with no intrinsic size. Do nothing.
				*
				* Clearing the size instead looks harmless and is not: null
				* dimensions reach the DOCX exporter, which substitutes 200×200,
				* so a 1600×400 banner would export to Word as a distorted square.
				* Leaving the user's size alone is strictly better than replacing
				* it with one that is wrong everywhere else.
				*/
				return false;
				const columnWidth = editor.view.dom.clientWidth;
				const widthPx = columnWidth > 0 && naturalWidth > columnWidth ? Math.round(columnWidth) : naturalWidth;
				const heightPx = widthPx === naturalWidth ? naturalHeight : Math.round(naturalHeight / naturalWidth * widthPx);
				return commands.updateAttributes(this.name, {
					widthPx,
					heightPx
				});
			},
			setImageWidth: (widthPx) => (props) => {
				const { editor } = props;
				const current = editor.getAttributes(this.name);
				const intrinsicWidth = current["widthPx"];
				const intrinsicHeight = current["heightPx"];
				const width = Math.max(24, Math.round(widthPx));
				const height = typeof intrinsicWidth === "number" && typeof intrinsicHeight === "number" && intrinsicWidth > 0 ? Math.round(intrinsicHeight / intrinsicWidth * width) : void 0;
				return updateSelectedImage(props, this.name, {
					widthPx: width,
					...height === void 0 ? {} : { heightPx: height }
				});
			},
			setImageAlt: (alt) => (props) => updateSelectedImage(props, this.name, { alt })
		};
	}
});
//#endregion
//#region src/pagination/pagination-plugin.ts
/**
* Holds the spacer decorations that turn one continuous flow into pages.
*
* The spacers are decorations, never document content: page breaks are derived
* from measurement and must not end up in the saved file, where they would
* become wrong the moment the margins or the font changed.
*/
const paginationPluginKey = new PluginKey("burfyPagination");
function createPaginationPlugin() {
	return new Plugin({
		key: paginationPluginKey,
		state: {
			init: () => DecorationSet.empty,
			apply(tr, value) {
				const meta = tr.getMeta(paginationPluginKey);
				if (meta) return meta.decorations;
				return value.map(tr.mapping, tr.doc);
			}
		},
		props: { decorations: (state) => paginationPluginKey.getState(state) ?? DecorationSet.empty }
	});
}
/**
* A break spacer: the unused remainder of a page, and the chrome that closes
* it — bottom margin, footer, and the visual gap to the next sheet.
*
* It stops there, at the top edge of the next sheet. The rest of the way, down
* to that page's content box, is a `page-top` spacer instead, because the two
* halves print differently: on paper the printer's own page break replaces
* everything this element stands for, while the next page's top margin has to
* survive as real space.
*
* `inline-block` at full width so it behaves the same whether it lands between
* two paragraphs or inside one. Inside a paragraph it takes a line of its own
* and the following text resumes below it, which is exactly the effect a page
* break needs; a block-level element would be invalid there.
*/
function createSpacerDecoration(pos, heightPx) {
	return spacerWidget(pos, heightPx, "burfy-docs-page-spacer", SIDE_BREAK);
}
/**
* The space a page opens with: its top margin and header band, plus however
* far down the content rests when the page is vertically aligned.
*
* A separate element from the break spacer, and the whole point of the split:
* this one keeps its height when printing. It is the same element on screen
* and on paper, so the two cannot drift — where the content sits down the page
* is decided once.
*/
function createPageTopDecoration(pos, heightPx) {
	return spacerWidget(pos, heightPx, "burfy-docs-page-top", SIDE_PAGE_TOP);
}
/**
* Where each piece of break chrome sits relative to the others at one
* position: the break, then the next page's top space, then any repeated
* table header. All negative but the headers, so text typed at a break lands
* after the chrome rather than inside it.
*/
const SIDE_BREAK = -2;
const SIDE_PAGE_TOP = -1;
function spacerWidget(pos, heightPx, className, side) {
	return Decoration.widget(pos, () => {
		const element = document.createElement("span");
		element.className = className;
		element.style.height = `${heightPx}px`;
		element.contentEditable = "false";
		element.setAttribute("aria-hidden", "true");
		return element;
	}, {
		side,
		marks: [],
		ignoreSelection: true
	});
}
function createDecorationSet(doc, decorations) {
	return DecorationSet.create(doc, decorations);
}
/**
* A page break *inside* a table.
*
* A bare spacer will not do here: between two `<tr>`s the browser wraps stray
* content in an anonymous box whose behaviour varies, and any gap drawn inside
* a cell would stretch that cell's borders down the length of the page break.
*
* So the gap is a real row with one borderless, full-width cell — two rows, in
* fact, for the same reason the flow has two spacers: one for the break, one
* for the top margin the continuation page opens with. Behind them the header
* rows the layout engine already charged for are cloned in. A table continuing
* overleaf without its column headings is unreadable, and reserving room for a
* header that never appears would leave a mystery gap.
*
* Returned as several decorations because a widget renders exactly one node.
*/
function createTableBreakDecorations(pos, gapPx, pageTopPx, columnCount, headerRows) {
	const spacerRow = (className, heightPx, side) => Decoration.widget(pos, () => {
		const row = document.createElement("tr");
		row.className = className;
		row.contentEditable = "false";
		row.setAttribute("aria-hidden", "true");
		const cell = document.createElement("td");
		cell.colSpan = Math.max(1, columnCount);
		cell.style.height = `${heightPx}px`;
		row.append(cell);
		return row;
	}, {
		side,
		marks: [],
		ignoreSelection: true
	});
	const rows = [spacerRow("burfy-docs-page-spacer burfy-docs-table-break", gapPx, SIDE_BREAK)];
	if (pageTopPx > 0) rows.push(spacerRow("burfy-docs-page-top burfy-docs-table-top", pageTopPx, SIDE_PAGE_TOP));
	const repeated = headerRows.map((headerRow, index) => Decoration.widget(pos, () => {
		const clone = headerRow.cloneNode(true);
		clone.classList.add("burfy-docs-repeated-header");
		clone.contentEditable = "false";
		clone.setAttribute("aria-hidden", "true");
		return clone;
	}, {
		side: index,
		marks: [],
		ignoreSelection: true
	}));
	return [...rows, ...repeated];
}
//#endregion
//#region src/pagination/pagination-extension.ts
/**
* Registers the decoration store that holds the page spacers.
*
* Adds no node or mark, so the document's vocabulary is unchanged — page
* breaks are a rendering concern and must never reach the saved file.
*/
const Pagination = Extension.create({
	name: "pagination",
	addProseMirrorPlugins() {
		return [createPaginationPlugin()];
	}
});
//#endregion
//#region src/search/document-text.ts
/**
* Flatten a document to text.
*
* Block boundaries become newlines so that a search for "end start" cannot
* match across a paragraph break — and so offsets stay aligned, since the
* newline occupies one character and the boundary occupies document positions
* of its own.
*/
function flattenDocument(doc) {
	const segments = [];
	let text = "";
	let previousBlockEnd = null;
	doc.descendants((node, pos) => {
		if (node.isText) {
			if (previousBlockEnd !== null && previousBlockEnd !== pos) {
				text += "\n";
				previousBlockEnd = null;
			}
			segments.push({
				offset: text.length,
				pos,
				length: node.nodeSize
			});
			text += node.text ?? "";
			previousBlockEnd = pos + node.nodeSize;
			return false;
		}
		if (node.isBlock && text.length > 0) previousBlockEnd = previousBlockEnd ?? -1;
		return true;
	});
	return {
		text,
		segments
	};
}
/**
* Map a flat-text offset back to a document position.
*
* Returns `null` for an offset that lands on a synthesised separator rather
* than on real text — there is no position for a character that is not in the
* document.
*/
function positionOf(flat, offset) {
	for (const segment of flat.segments) {
		if (offset < segment.offset) return null;
		if (offset < segment.offset + segment.length) return segment.pos + (offset - segment.offset);
	}
	return null;
}
/** Map a flat-text range to a document range, or `null` if it does not map. */
function rangeOf(flat, start, end) {
	const from = positionOf(flat, start);
	const lastCharacter = positionOf(flat, end - 1);
	if (from === null || lastCharacter === null) return null;
	return {
		from,
		to: lastCharacter + 1
	};
}
//#endregion
//#region src/search/useSearch.ts
/**
* Find and replace.
*
* Matches are decorations, never content: highlighting by wrapping text in a
* mark would put the search state into the document and into the saved file.
*/
const searchPluginKey = new PluginKey("burfySearch");
function createSearchPlugin() {
	return new Plugin({
		key: searchPluginKey,
		state: {
			init: () => DecorationSet.empty,
			apply(tr, value) {
				const meta = tr.getMeta(searchPluginKey);
				if (meta) return meta;
				return value.map(tr.mapping, tr.doc);
			}
		},
		props: { decorations: (state) => searchPluginKey.getState(state) ?? DecorationSet.empty }
	});
}
function useSearch(editor, active) {
	const [query, setQuery] = useState("");
	const [options, setOptions] = useState({});
	const [activeIndex, setActiveIndex] = useState(-1);
	/** Bumped to recompute after an edit. */
	const [revision, setRevision] = useState(0);
	useEffect(() => {
		if (!editor) return;
		const onUpdate = () => setRevision((value) => value + 1);
		editor.on("update", onUpdate);
		return () => {
			editor.off("update", onUpdate);
		};
	}, [editor]);
	const matches = useMemo(() => {
		if (!editor || !active || !query) return [];
		const flat = flattenDocument(editor.state.doc);
		return findMatches(flat.text, query, options).flatMap((match) => {
			const range = rangeOf(flat, match.start, match.end);
			return range ? [{
				...match,
				...range
			}] : [];
		});
	}, [
		editor,
		active,
		query,
		options,
		revision
	]);
	useEffect(() => {
		setActiveIndex(matches.length === 0 ? -1 : 0);
	}, [matches.length, query]);
	useEffect(() => {
		const view = editor?.view;
		if (!view || view.isDestroyed) return;
		const decorations = matches.map((match, index) => Decoration.inline(match.from, match.to, { class: index === activeIndex ? "burfy-docs-search-match burfy-docs-search-active" : "burfy-docs-search-match" }));
		view.dispatch(view.state.tr.setMeta(searchPluginKey, DecorationSet.create(view.state.doc, decorations)).setMeta("addToHistory", false));
	}, [
		editor,
		matches,
		activeIndex
	]);
	/** Scroll a match into view and put the caret on it. */
	const reveal = useCallback((index) => {
		const view = editor?.view;
		const match = matches[index];
		if (!view || !match) return;
		view.dispatch(view.state.tr.setSelection(TextSelection.create(view.state.doc, match.from, match.to)).scrollIntoView());
	}, [editor, matches]);
	return {
		query,
		setQuery,
		options,
		setOptions,
		matches,
		activeIndex,
		next: useCallback(() => {
			if (matches.length === 0) return;
			const index = (activeIndex + 1) % matches.length;
			setActiveIndex(index);
			reveal(index);
		}, [
			matches.length,
			activeIndex,
			reveal
		]),
		previous: useCallback(() => {
			if (matches.length === 0) return;
			const index = (activeIndex - 1 + matches.length) % matches.length;
			setActiveIndex(index);
			reveal(index);
		}, [
			matches.length,
			activeIndex,
			reveal
		]),
		replaceCurrent: useCallback((replacement) => {
			const view = editor?.view;
			const match = matches[activeIndex];
			if (!view || !match) return;
			view.dispatch(view.state.tr.insertText(replacement, match.from, match.to));
		}, [
			editor,
			matches,
			activeIndex
		]),
		replaceAll: useCallback((replacement) => {
			const view = editor?.view;
			if (!view || matches.length === 0) return;
			const tr = view.state.tr;
			for (let index = matches.length - 1; index >= 0; index -= 1) {
				const match = matches[index];
				if (match) tr.insertText(replacement, match.from, match.to);
			}
			view.dispatch(tr);
		}, [editor, matches])
	};
}
//#endregion
//#region src/search/search-extension.ts
/**
* Registers the decoration store that holds search highlights.
*
* Adds no node or mark: highlighting matches by wrapping them in a mark would
* put transient search state into the document, and from there into the file.
*/
const Search = Extension.create({
	name: "burfySearch",
	addProseMirrorPlugins() {
		return [createSearchPlugin()];
	}
});
//#endregion
//#region src/voice/interim-decoration.ts
/**
* Interim speech, painted rather than inserted.
*
* An engine emits guesses before it commits. Inserting a guess and replacing
* it when it firms up is the obvious implementation and a trap: half-heard
* words end up in the undo stack, in `onChange`, in autosave and in the saved
* file, and a crash mid-sentence preserves nonsense.
*
* So the guess is a widget decoration at the caret — visible, greyed,
* unselectable, and not part of the document. Only a *final* result becomes a
* transaction, which is also why one spoken sentence is one undo step.
*
* **The widget's element is created once and its text mutated in place.** The
* obvious version dispatched a transaction per guess, and an engine emits ten
* or more a second: every one of them re-ran the toolbar's `useEditorState`
* selector and re-rendered the page stack, which is what made dictation feel
* slow on a long document. A transaction is now dispatched only when the ghost
* appears or disappears — twice an utterance instead of a hundred times.
*/
const dictationPluginKey = new PluginKey("burfyDictation");
function createGhostElement() {
	const span = document.createElement("span");
	span.className = "burfy-docs-dictation-ghost";
	span.setAttribute("aria-live", "polite");
	return span;
}
function createDictationPlugin() {
	return new Plugin({
		key: dictationPluginKey,
		state: {
			init: () => ({
				element: createGhostElement(),
				visible: false
			}),
			apply(tr, value) {
				const meta = tr.getMeta(dictationPluginKey);
				if (meta !== void 0) return {
					...value,
					visible: meta
				};
				if (tr.docChanged && value.visible) {
					value.element.textContent = "";
					return {
						...value,
						visible: false
					};
				}
				return value;
			}
		},
		props: { decorations(state) {
			const ghost = dictationPluginKey.getState(state);
			if (!ghost?.visible) return DecorationSet.empty;
			return DecorationSet.create(state.doc, [Decoration.widget(state.selection.head, ghost.element, {
				side: -1,
				ignoreSelection: true,
				key: "burfy-dictation-ghost"
			})]);
		} }
	});
}
/** The guess currently on screen. `''` when there is none. */
function interimText(state) {
	const ghost = dictationPluginKey.getState(state);
	if (!ghost?.visible) return "";
	return ghost.element.textContent ?? "";
}
/**
* How much the guess must grow before the view is scrolled again.
*
* Scrolling forces a layout read, and a guess changes ten times a second.
* Every few words is enough to keep a long sentence on screen without doing
* that work on every syllable.
*/
const SCROLL_EVERY_CHARS = 12;
/** Length at which each ghost was last scrolled into view. */
const scrolledAt = /* @__PURE__ */ new WeakMap();
function keepInView(element, text) {
	const last = scrolledAt.get(element) ?? 0;
	if (text.length !== 0 && Math.abs(text.length - last) < SCROLL_EVERY_CHARS) return;
	scrolledAt.set(element, text.length);
	if (typeof element.scrollIntoView !== "function") return;
	try {
		element.scrollIntoView({
			block: "nearest",
			inline: "nearest"
		});
	} catch {}
}
/**
* Show (or with `''`, clear) the interim text.
*
* Never touches the document, and usually does not even dispatch: while the
* ghost is already on screen, a new guess is one `textContent` assignment.
*/
function setInterimText(view, text) {
	if (view.isDestroyed) return;
	const ghost = dictationPluginKey.getState(view.state);
	if (!ghost) return;
	if (ghost.element.textContent !== text) {
		ghost.element.textContent = text;
		keepInView(ghost.element, text);
	}
	const shouldShow = text.length > 0;
	if (shouldShow === ghost.visible) return;
	view.dispatch(view.state.tr.setMeta(dictationPluginKey, shouldShow));
}
/** Registers the store the interim text lives in. Adds no node and no mark. */
const Dictation = Extension.create({
	name: "burfyDictation",
	addProseMirrorPlugins() {
		return [createDictationPlugin()];
	}
});
//#endregion
//#region src/extensions/index.ts
/**
* The document's editing vocabulary.
*
* Every node and mark registered here must appear in `@burfy/docs-core`'s
* registry, and vice versa — `extensions.test.ts` asserts the two agree.
* Without that check the two drift, and the symptom is silent content loss on
* load rather than an error anyone can act on.
*/
function createExtensions(options = {}) {
	return [
		StarterKit.configure({
			heading: { levels: [...HEADING_LEVELS] },
			link: {
				openOnClick: false,
				autolink: true,
				defaultProtocol: "https",
				protocols: [
					"http",
					"https",
					"mailto",
					"tel"
				],
				HTMLAttributes: {
					rel: "noopener noreferrer nofollow",
					target: "_blank"
				}
			}
		}),
		TextStyle,
		Color,
		BackgroundColor,
		FontFamily,
		FontSize,
		Highlight.configure({ multicolor: true }),
		Subscript,
		Superscript,
		TextAlign.configure({ types: [NODE_TYPES.heading, NODE_TYPES.paragraph] }),
		ParagraphStyle,
		PageBreak,
		TranslitSource,
		TaskList,
		TaskItem.configure({ nested: true }),
		ImageNode,
		Table.configure({
			resizable: true,
			lastColumnResizable: false
		}),
		TableRow,
		TableHeaderWithProperties,
		TableCellWithProperties,
		Placeholder.configure({ placeholder: options.placeholder ?? "Start writing…" }),
		Pagination,
		Search,
		Dictation
	];
}
//#endregion
//#region src/paste/sanitize-html.ts
/**
* Sanitise HTML arriving from the clipboard or a drop.
*
* This is the product's main untrusted-input surface: people paste from Word,
* Google Docs and arbitrary web pages, and that HTML carries scripts, event
* handlers, remote references and enough inline CSS to break the page out of
* its own layout.
*
* Two layers, and both are needed:
*
*   1. **DOMPurify** removes scripts, event handlers and dangerous URLs.
*   2. **The schema** is the real allowlist — ProseMirror parses the result
*      with our node and mark specs, so anything with no mapping is dropped
*      no matter how well-formed it is.
*
* The tag list below is therefore not a security boundary on its own; it
* exists so the parser sees a tidy tree, and so `style` can be narrowed to
* properties that carry meaning we actually keep.
*/
/** Tags that map onto something in our schema. */
const ALLOWED_TAGS = [
	"p",
	"br",
	"span",
	"div",
	"h1",
	"h2",
	"h3",
	"h4",
	"h5",
	"h6",
	"strong",
	"b",
	"em",
	"i",
	"u",
	"s",
	"strike",
	"del",
	"sub",
	"sup",
	"mark",
	"code",
	"pre",
	"blockquote",
	"hr",
	"ul",
	"ol",
	"li",
	"a",
	"table",
	"thead",
	"tbody",
	"tfoot",
	"tr",
	"th",
	"td",
	"caption",
	"colgroup",
	"col"
];
const ALLOWED_ATTR = [
	"href",
	"title",
	"start",
	"type",
	"style",
	"data-checked",
	"colspan",
	"rowspan",
	"span"
];
/**
* Inline CSS properties worth preserving.
*
* Everything else goes. This matters more than it looks: Word and Google Docs
* express bold and italic as inline `font-weight` / `font-style`, so stripping
* `style` wholesale would silently flatten every pasted document. Conversely,
* allowing `position`, `z-index` or `content` would let a paste escape the
* page and overlay the application's own UI.
*/
const ALLOWED_STYLE_PROPERTIES = /* @__PURE__ */ new Set([
	"font-weight",
	"font-style",
	"font-family",
	"font-size",
	"text-decoration",
	"text-decoration-line",
	"color",
	"background-color",
	"text-align",
	"line-height",
	"margin-left",
	"margin-top",
	"margin-bottom",
	"vertical-align",
	"width",
	"border",
	"padding"
]);
/**
* Values that may smuggle a fetch or an expression through a property we
* otherwise allow — `background-color: url(...)`, legacy IE `expression()`,
* and any embedded scheme.
*/
const UNSAFE_VALUE = /url\s*\(|expression\s*\(|javascript:|data:|@import|<|\\/i;
/** Rebuild a `style` attribute from only the properties we accept. */
function sanitizeStyleAttribute(style) {
	const kept = [];
	for (const declaration of style.split(";")) {
		const separator = declaration.indexOf(":");
		if (separator === -1) continue;
		const property = declaration.slice(0, separator).trim().toLowerCase();
		const value = declaration.slice(separator + 1).trim();
		if (!value || !ALLOWED_STYLE_PROPERTIES.has(property)) continue;
		if (UNSAFE_VALUE.test(value)) continue;
		kept.push(`${property}: ${value}`);
	}
	return kept.join("; ");
}
/**
* Narrow every `style` attribute in the tree.
*
* Done as a walk over the sanitised DOM rather than through a DOMPurify hook:
* hooks are registered globally on the shared DOMPurify instance, so a library
* that installs one changes the behaviour of every other consumer in the app.
*/
function narrowInlineStyles(root) {
	for (const element of root.querySelectorAll("[style]")) {
		const safe = sanitizeStyleAttribute(element.getAttribute("style") ?? "");
		if (safe) element.setAttribute("style", safe);
		else element.removeAttribute("style");
	}
}
function sanitizePastedHtml(html) {
	const fragment = DOMPurify.sanitize(html, {
		ALLOWED_TAGS,
		ALLOWED_ATTR,
		ALLOW_DATA_ATTR: false,
		FORBID_TAGS: [
			"img",
			"picture",
			"source",
			"style",
			"script",
			"iframe",
			"object"
		],
		RETURN_DOM_FRAGMENT: true
	});
	narrowInlineStyles(fragment);
	const container = fragment.ownerDocument.createElement("div");
	container.append(fragment);
	return container.innerHTML;
}
//#endregion
//#region src/ui/useDismissable.ts
/**
* Close a popover on Escape, or on a click outside it.
*
* Both new popovers had an `onKeyDown` on the popover element, which never
* ran: the toolbar button prevents mousedown's default so focus stays in
* ProseMirror, nothing inside the popover is focused, and a keystroke the
* document never routes there cannot bubble out of it. The menu then floated
* over the page until its own button was clicked again.
*
* So both listeners go on the document, where the events actually are.
*/
function useDismissable(open, onDismiss) {
	const ref = useRef(null);
	const dismiss = useRef(onDismiss);
	dismiss.current = onDismiss;
	useEffect(() => {
		if (!open) return;
		const onKeyDown = (event) => {
			if (event.key !== "Escape") return;
			event.preventDefault();
			dismiss.current();
		};
		const onPointerDown = (event) => {
			const target = event.target;
			if (target instanceof Node && ref.current?.parentElement?.contains(target)) return;
			dismiss.current();
		};
		document.addEventListener("keydown", onKeyDown, true);
		document.addEventListener("pointerdown", onPointerDown, true);
		return () => {
			document.removeEventListener("keydown", onKeyDown, true);
			document.removeEventListener("pointerdown", onPointerDown, true);
		};
	}, [open]);
	return ref;
}
//#endregion
//#region src/ui/ToolbarColorMenu.tsx
function ToolbarColorMenu({ label, value, swatches, onChange, onClear, children }) {
	const [open, setOpen] = useState(false);
	const close = () => setOpen(false);
	const popover = useDismissable(open, close);
	/**
	* `<input type="color">` and the pressed state both need six-digit hex.
	*
	* A highlight can arrive as `rgb(...)` — that is what the DOM makes of a
	* colour on pasted HTML — and the input silently rewrites anything else to
	* black, so the swatch would read black and a nudge would commit black.
	*/
	const hex = toHexColor(value);
	return /* @__PURE__ */ jsxs("span", {
		className: "burfy-docs-tb-link",
		children: [/* @__PURE__ */ jsx(ToolbarButton, {
			label,
			active: value !== null,
			onClick: () => setOpen((current) => !current),
			children: /* @__PURE__ */ jsxs("span", {
				className: "burfy-docs-tb-swatch-icon",
				children: [children, /* @__PURE__ */ jsx("span", {
					className: "burfy-docs-tb-swatch-bar",
					"data-none": value === null || void 0,
					style: value ? { background: value } : void 0
				})]
			})
		}), open && /* @__PURE__ */ jsxs("span", {
			ref: popover,
			className: "burfy-docs-tb-popover burfy-docs-colour-menu",
			role: "dialog",
			"aria-label": label,
			children: [/* @__PURE__ */ jsxs("span", {
				className: "burfy-docs-colour-swatches",
				children: [/* @__PURE__ */ jsx("button", {
					type: "button",
					className: "burfy-docs-colour-swatch",
					"data-none": true,
					title: "None",
					"aria-label": "None",
					"aria-pressed": value === null,
					onMouseDown: (event) => event.preventDefault(),
					onClick: () => {
						onClear();
						close();
					}
				}), swatches.map((swatch) => /* @__PURE__ */ jsx("button", {
					type: "button",
					className: "burfy-docs-colour-swatch",
					title: swatch.name,
					"aria-label": swatch.name,
					"aria-pressed": hex === toHexColor(swatch.color),
					style: { background: swatch.color },
					onMouseDown: (event) => event.preventDefault(),
					onClick: () => {
						onChange(swatch.color);
						close();
					}
				}, swatch.color))]
			}), /* @__PURE__ */ jsx("span", {
				className: "burfy-docs-colour-actions",
				children: /* @__PURE__ */ jsxs("label", {
					className: "burfy-docs-cell-clear",
					children: ["Custom…", /* @__PURE__ */ jsx("input", {
						type: "color",
						"aria-label": `${label}: custom colour`,
						value: hex ?? "#ffff00",
						onChange: (event) => onChange(event.target.value)
					})]
				})
			})]
		})]
	});
}
//#endregion
//#region src/ui/PageBackgroundControl.tsx
function PageBackgroundControl({ pageSetup, onPageSetupChange }) {
	const [open, setOpen] = useState(false);
	const popover = useDismissable(open, () => setOpen(false));
	const background = pageSetup.background ?? NO_PAGE_BACKGROUND;
	return /* @__PURE__ */ jsxs("span", {
		className: "burfy-docs-tb-link",
		children: [/* @__PURE__ */ jsx(ToolbarButton, {
			label: "Page background",
			active: background.kind !== "none",
			onClick: () => setOpen((current) => !current),
			children: /* @__PURE__ */ jsx(Icon, { name: "page-background" })
		}), open && /* @__PURE__ */ jsxs("span", {
			ref: popover,
			className: "burfy-docs-tb-popover burfy-docs-page-background-popover",
			role: "dialog",
			"aria-label": "Page background",
			children: [/* @__PURE__ */ jsx(PageBackgroundFields, {
				value: background,
				onChange: (next) => onPageSetupChange({
					...pageSetup,
					background: next
				})
			}), /* @__PURE__ */ jsx("button", {
				type: "button",
				className: "burfy-docs-cell-clear",
				onClick: () => setOpen(false),
				children: "Done"
			})]
		})]
	});
}
//#endregion
//#region src/ui/LinkControl.tsx
/**
* Link insertion.
*
* A small inline form rather than `window.prompt`: prompt blocks the whole
* page, cannot be styled or dismissed gracefully, and is suppressed outright
* in some embedding contexts.
*/
/** Schemes the editor will write into an href. Mirrors the Link extension. */
const ALLOWED_PROTOCOLS = [
	"http:",
	"https:",
	"mailto:",
	"tel:"
];
/**
* Normalise user input into a safe absolute URL, or `null` if it is not one.
*
* A bare `example.com` becomes `https://example.com`; anything whose scheme is
* not in the allowlist — `javascript:` above all — is refused rather than
* "cleaned", because a cleaned-up hostile URL is still a hostile URL.
*/
function normaliseLinkHref(input) {
	const trimmed = input.trim();
	if (!trimmed) return null;
	const candidate = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
	try {
		const url = new URL(candidate);
		return ALLOWED_PROTOCOLS.includes(url.protocol) ? url.toString() : null;
	} catch {
		return null;
	}
}
function LinkControl({ editor, active }) {
	const [open, setOpen] = useState(false);
	const [value, setValue] = useState("");
	const [error, setError] = useState(null);
	function openForm() {
		if (active) {
			editor.chain().focus().unsetLink().run();
			return;
		}
		const existing = editor.getAttributes("link")["href"];
		setValue(typeof existing === "string" ? existing : "");
		setError(null);
		setOpen(true);
	}
	function apply() {
		const href = normaliseLinkHref(value);
		if (!href) {
			setError("Enter a web address, e.g. example.com");
			return;
		}
		editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
		setOpen(false);
		setValue("");
	}
	return /* @__PURE__ */ jsxs("span", {
		className: "burfy-docs-tb-link",
		children: [/* @__PURE__ */ jsx(ToolbarButton, {
			label: active ? "Remove link" : "Insert link",
			active,
			onClick: openForm,
			shortcut: "Ctrl+K",
			children: /* @__PURE__ */ jsx(Icon, { name: "link" })
		}), open && /* @__PURE__ */ jsxs("span", {
			className: "burfy-docs-tb-popover",
			role: "dialog",
			"aria-label": "Insert link",
			children: [
				/* @__PURE__ */ jsx("input", {
					className: "burfy-docs-tb-input",
					type: "text",
					autoFocus: true,
					placeholder: "example.com",
					"aria-label": "Link address",
					"aria-invalid": error !== null,
					value,
					onChange: (event) => {
						setValue(event.target.value);
						setError(null);
					},
					onKeyDown: (event) => {
						if (event.key === "Enter") {
							event.preventDefault();
							apply();
						} else if (event.key === "Escape") {
							event.preventDefault();
							setOpen(false);
						}
					}
				}),
				/* @__PURE__ */ jsx("button", {
					type: "button",
					className: "burfy-docs-tb-button",
					onClick: apply,
					children: "Apply"
				}),
				error && /* @__PURE__ */ jsx("span", {
					className: "burfy-docs-tb-error",
					role: "alert",
					children: error
				})
			]
		})]
	});
}
//#endregion
//#region src/ime/LanguagePicker.tsx
/**
* Input-language picker, with the privacy note beside it.
*
* The note is deliberately here rather than buried in a policy page: choosing
* a non-English language starts sending the roman words being typed to a
* Google endpoint, and that is worth knowing at the moment the choice is made,
* not afterwards.
*/
function LanguagePicker({ value, onChange }) {
	const languages = listLanguages();
	const sendsText = languages.find((language) => language.code === value)?.transliterable === true;
	return /* @__PURE__ */ jsxs("span", {
		className: "burfy-docs-lang",
		children: [/* @__PURE__ */ jsx("select", {
			className: "burfy-docs-tb-select",
			"aria-label": "Input language",
			title: "Input language (Ctrl+G re-opens suggestions on a converted word)",
			value,
			onChange: (event) => onChange(event.target.value),
			children: languages.map((language) => /* @__PURE__ */ jsx("option", {
				value: language.code,
				children: language.nativeLabel
			}, language.code))
		}), sendsText && /* @__PURE__ */ jsx("span", {
			className: "burfy-docs-lang-note",
			title: "Typed words are sent to Google Input Tools to fetch suggestions. Nothing is sent while you type in English.",
			"aria-label": "Typed words are sent to Google Input Tools to fetch suggestions",
			children: "suggestions via Google"
		})]
	});
}
//#endregion
//#region src/voice/VoiceButton.tsx
/**
* The microphone.
*
* Hidden outright when no engine can run — a permanently disabled control that
* cannot explain itself is worse than no control. Where it *is* shown, the
* tooltip names both the language being recognised and where the audio goes:
* the same standard the language picker is held to, and for the same reason.
*/
function VoiceButton({ dictation, languageLabel }) {
	if (!dictation.supported) return null;
	const starting = dictation.status === "starting";
	const label = dictation.listening ? "Stop voice typing" : "Voice typing";
	const destination = dictation.audioDestination ? ` Audio is sent to ${dictation.audioDestination}.` : "";
	return /* @__PURE__ */ jsxs("span", {
		className: "burfy-docs-voice",
		children: [/* @__PURE__ */ jsx(ToolbarButton, {
			label,
			shortcut: "Ctrl+Shift+S",
			title: `${label} in ${languageLabel} — ${dictation.locale} (Ctrl+Shift+S).${destination}`,
			active: dictation.listening,
			onClick: dictation.toggle,
			children: /* @__PURE__ */ jsx("span", {
				className: "burfy-docs-voice-icon",
				"data-listening": dictation.status === "listening" || void 0,
				"data-starting": starting || void 0,
				children: /* @__PURE__ */ jsx(Icon, { name: "mic" })
			})
		}), dictation.listening && /* @__PURE__ */ jsx("span", {
			className: "burfy-docs-voice-status",
			role: "status",
			children: starting ? "Starting…" : `Listening… ${languageLabel}`
		})]
	});
}
//#endregion
//#region src/ui/cell-commands.ts
/**
* Setting attributes on every cell in the selection.
*
* Not `setCellAttribute`. `prosemirror-tables`' `setCellAttr` reads one
* reference cell and bails for the *whole* selection when that cell already
* holds the value being set — so clearing a row where only the first cell was
* shaded did nothing at all, silently. Writing the markup directly has no such
* shortcut.
*
* It also takes several attributes at once, which is what makes "clear all
* formatting" one transaction and therefore one undo step rather than five.
*/
function isCell(name) {
	return name === NODE_TYPES.tableCell || name === NODE_TYPES.tableHeader;
}
function cellPositions(editor) {
	const { selection, doc } = editor.state;
	if (selection instanceof CellSelection) {
		const positions = [];
		selection.forEachCell((_cell, pos) => positions.push(pos));
		return positions;
	}
	/**
	* A plain text selection can still span cells — Ctrl+A inside a table is a
	* `TextSelection`, not a `CellSelection`, and someone who selects everything
	* and clears the formatting means all of it.
	*/
	if (!selection.empty) {
		const spanned = [];
		doc.nodesBetween(selection.from, selection.to, (node, pos) => {
			if (isCell(node.type.name)) spanned.push(pos);
		});
		if (spanned.length > 0) return spanned;
	}
	const $from = selection.$from;
	for (let depth = $from.depth; depth > 0; depth -= 1) if (isCell($from.node(depth).type.name)) {
		const pos = $from.before(depth);
		return doc.nodeAt(pos) ? [pos] : [];
	}
	return [];
}
function setCellAttributes(editor, changes) {
	const positions = cellPositions(editor);
	if (positions.length === 0) return false;
	const { tr } = editor.state;
	for (const pos of positions) {
		const cell = tr.doc.nodeAt(pos);
		if (!cell) continue;
		tr.setNodeMarkup(pos, void 0, {
			...cell.attrs,
			...changes
		});
	}
	if (!tr.docChanged) return false;
	editor.view.dispatch(tr);
	return true;
}
//#endregion
//#region src/ui/CellPropertiesControl.tsx
/**
* Cell formatting for the current selection.
*
* `setCellAttribute` applies to every selected cell, so dragging across a row
* and setting a background shades the row — which is what anyone expects, and
* what makes this usable without a separate "apply to" control.
*/
/** Read the properties of the cell the caret is in. */
function currentCellProperties(editor) {
	const { $from } = editor.state.selection;
	for (let depth = $from.depth; depth > 0; depth -= 1) {
		const node = $from.node(depth);
		if (node.type.name === "tableCell" || node.type.name === "tableHeader") return readCellProperties(node.attrs);
	}
	return readCellProperties(null);
}
function CellPropertiesControl({ editor }) {
	const [open, setOpen] = useState(false);
	/**
	* Subscribed, not read once.
	*
	* Reading `editor.state` during render looks like it works and does not: the
	* component has no reason to re-render when the caret moves, so the fields
	* kept showing the properties of whichever cell happened to be selected when
	* the popover was first drawn — and did not update after its own edits.
	*/
	const properties = useEditorState({
		editor,
		selector: ({ editor: instance }) => currentCellProperties(instance)
	});
	const set = (name, value) => {
		setCellAttributes(editor, { [name]: value });
		editor.commands.focus();
	};
	return /* @__PURE__ */ jsxs("span", {
		className: "burfy-docs-tb-link",
		children: [/* @__PURE__ */ jsx(ToolbarButton, {
			label: "Cell properties",
			active: open,
			onClick: () => setOpen((current) => !current),
			children: /* @__PURE__ */ jsx(Icon, { name: "cell-properties" })
		}), open && /* @__PURE__ */ jsxs("span", {
			className: "burfy-docs-tb-popover burfy-docs-cell-popover",
			role: "dialog",
			"aria-label": "Cell properties",
			onKeyDown: (event) => {
				if (event.key !== "Escape") return;
				event.preventDefault();
				setOpen(false);
				editor.commands.focus();
			},
			children: [
				/* @__PURE__ */ jsxs("label", {
					className: "burfy-docs-cell-field",
					children: [/* @__PURE__ */ jsx("span", { children: "Background" }), /* @__PURE__ */ jsxs("span", {
						className: "burfy-docs-cell-color",
						children: [/* @__PURE__ */ jsx("input", {
							type: "color",
							"aria-label": "Cell background colour",
							value: toHexColor(properties.backgroundColor) ?? "#ffffff",
							onChange: (event) => set("backgroundColor", event.target.value)
						}), /* @__PURE__ */ jsx("button", {
							type: "button",
							className: "burfy-docs-cell-clear",
							onClick: () => set("backgroundColor", null),
							children: "Clear"
						})]
					})]
				}),
				/* @__PURE__ */ jsxs("label", {
					className: "burfy-docs-cell-field",
					children: [/* @__PURE__ */ jsx("span", { children: "Vertical align" }), /* @__PURE__ */ jsxs("select", {
						className: "burfy-docs-tb-select",
						"aria-label": "Vertical alignment",
						value: properties.verticalAlign ?? "",
						onChange: (event) => set("verticalAlign", event.target.value || null),
						children: [/* @__PURE__ */ jsx("option", {
							value: "",
							children: "Default"
						}), CELL_VERTICAL_ALIGNS.map((align) => /* @__PURE__ */ jsx("option", {
							value: align,
							children: align.charAt(0).toUpperCase() + align.slice(1)
						}, align))]
					})]
				}),
				/* @__PURE__ */ jsxs("label", {
					className: "burfy-docs-cell-field",
					children: [/* @__PURE__ */ jsx("span", { children: "Padding" }), /* @__PURE__ */ jsx("input", {
						className: "burfy-docs-tb-input burfy-docs-cell-number",
						type: "number",
						min: 0,
						max: MAX_PADDING_PX,
						"aria-label": "Cell padding in pixels",
						value: properties.paddingPx ?? "",
						placeholder: "—",
						onChange: (event) => set("paddingPx", event.target.value === "" ? null : Number(event.target.value))
					})]
				}),
				/* @__PURE__ */ jsxs("label", {
					className: "burfy-docs-cell-field",
					children: [/* @__PURE__ */ jsx("span", { children: "Border" }), /* @__PURE__ */ jsxs("span", {
						className: "burfy-docs-cell-color",
						children: [/* @__PURE__ */ jsx("input", {
							className: "burfy-docs-tb-input burfy-docs-cell-number",
							type: "number",
							min: 0,
							max: MAX_BORDER_WIDTH_PX,
							"aria-label": "Border width in pixels",
							value: properties.borderWidthPx ?? "",
							placeholder: "—",
							onChange: (event) => set("borderWidthPx", event.target.value === "" ? null : Number(event.target.value))
						}), /* @__PURE__ */ jsx("input", {
							type: "color",
							"aria-label": "Border colour",
							value: toHexColor(properties.borderColor) ?? "#000000",
							onChange: (event) => set("borderColor", event.target.value)
						})]
					})]
				}),
				/* @__PURE__ */ jsx("button", {
					type: "button",
					className: "burfy-docs-cell-clear burfy-docs-cell-reset",
					onClick: () => {
						setCellAttributes(editor, Object.fromEntries(CELL_PROPERTY_ATTRIBUTES.map((name) => [name, null])));
						editor.commands.focus();
					},
					children: "Clear all formatting"
				})
			]
		})]
	});
}
//#endregion
//#region src/ui/TableControls.tsx
/**
* Table actions.
*
* Insertion is always available; everything else appears only when the caret
* is actually in a table, so the toolbar does not carry eight permanently
* disabled buttons.
*/
function TableControls({ editor, inTable }) {
	const chain = () => editor.chain().focus();
	return /* @__PURE__ */ jsxs(Fragment, { children: [
		/* @__PURE__ */ jsx(ToolbarSeparator, {}),
		/* @__PURE__ */ jsx(ToolbarButton, {
			label: "Insert table",
			onClick: () => chain().insertTable({
				rows: 3,
				cols: 3,
				withHeaderRow: true
			}).run(),
			children: /* @__PURE__ */ jsx(Icon, { name: "table" })
		}),
		inTable && /* @__PURE__ */ jsxs(Fragment, { children: [
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Insert row above",
				onClick: () => chain().addRowBefore().run(),
				children: "⤒"
			}),
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Insert row below",
				onClick: () => chain().addRowAfter().run(),
				children: "⤓"
			}),
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Delete row",
				onClick: () => chain().deleteRow().run(),
				children: "⊖"
			}),
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Insert column left",
				onClick: () => chain().addColumnBefore().run(),
				children: "⇤"
			}),
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Insert column right",
				onClick: () => chain().addColumnAfter().run(),
				children: "⇥"
			}),
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Delete column",
				onClick: () => chain().deleteColumn().run(),
				children: "⊘"
			}),
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Toggle header row",
				onClick: () => chain().toggleHeaderRow().run(),
				children: "⊤"
			}),
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Merge or split cells",
				onClick: () => chain().mergeOrSplit().run(),
				children: "⧉"
			}),
			/* @__PURE__ */ jsx(CellPropertiesControl, { editor }),
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Delete table",
				onClick: () => chain().deleteTable().run(),
				children: "⌫▦"
			})
		] })
	] });
}
//#endregion
//#region src/ui/FontControls.tsx
/**
* Font family picker, and font upload where the provider supports it.
*
* Families are grouped by the language they are for. A writer of Malayalam
* should not have to scroll past every Latin family to reach the two that can
* render their text — and a Latin family selected for Malayalam text produces
* empty boxes, not a fallback anybody wants.
*/
function FontControls({ editor, provider, activeFamily, onError }) {
	const [families, setFamilies] = useState([]);
	const fileInput = useRef(null);
	const refresh = useCallback(() => {
		provider.list().then(setFamilies).catch(() => setFamilies([]));
	}, [provider]);
	useEffect(refresh, [refresh]);
	const apply = useCallback((family) => {
		provider.load(family).finally(() => {
			editor.chain().focus().setFontFamily(family).run();
		});
	}, [editor, provider]);
	const handleUpload = useCallback(async (file) => {
		if (!canUploadFonts(provider)) return;
		try {
			const bytes = new Uint8Array(await file.arrayBuffer());
			const added = await provider.upload(bytes, file.name);
			refresh();
			apply(added.family);
		} catch (error) {
			onError?.(error instanceof Error ? error.message : "That font could not be added.");
		}
	}, [
		provider,
		refresh,
		apply,
		onError
	]);
	const bundled = families.filter((font) => font.source === "bundled");
	const uploaded = families.filter((font) => font.source === "uploaded");
	const withLanguage = bundled.filter((font) => font.language);
	const latin = bundled.filter((font) => !font.language);
	return /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx(ToolbarSelect, {
		label: "Font",
		value: activeFamily ?? "",
		width: 150,
		options: [
			{
				value: "",
				label: "Default font"
			},
			...latin.map((font) => ({
				value: font.family,
				label: font.label
			})),
			...withLanguage.map((font) => ({
				value: font.family,
				label: font.label
			})),
			...uploaded.map((font) => ({
				value: font.family,
				label: `${font.label} ↑`
			}))
		],
		onChange: (family) => {
			if (family === "") editor.chain().focus().unsetFontFamily().run();
			else apply(family);
		}
	}), canUploadFonts(provider) && /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx(ToolbarButton, {
		label: "Upload a font",
		onClick: () => fileInput.current?.click(),
		children: /* @__PURE__ */ jsx(Icon, { name: "upload" })
	}), /* @__PURE__ */ jsx("input", {
		ref: fileInput,
		type: "file",
		accept: ".ttf,.otf,.woff,.woff2,font/ttf,font/otf,font/woff,font/woff2",
		hidden: true,
		onChange: (event) => {
			const file = event.target.files?.[0];
			event.target.value = "";
			if (file) handleUpload(file);
		}
	})] })] });
}
//#endregion
//#region src/ui/toolbar-state.ts
function readBlockStyle(editor) {
	for (const level of HEADING_LEVELS) if (editor.isActive(NODE_TYPES.heading, { level })) return `h${level}`;
	return "p";
}
function asNumber(value) {
	return typeof value === "number" && Number.isFinite(value) ? value : null;
}
function asString(value) {
	return typeof value === "string" && value.length > 0 ? value : null;
}
function readToolbarState(editor) {
	const textStyle = editor.getAttributes(MARK_TYPES.textStyle);
	const block = editor.isActive(NODE_TYPES.heading) ? editor.getAttributes(NODE_TYPES.heading) : editor.getAttributes(NODE_TYPES.paragraph);
	return {
		blockStyle: readBlockStyle(editor),
		textAlign: asString(block["textAlign"]) ?? "left",
		lineHeight: asNumber(block["lineHeight"]),
		fontSize: asString(textStyle["fontSize"]),
		color: asString(textStyle["color"]),
		fontFamily: asString(textStyle["fontFamily"]),
		highlight: editor.isActive("highlight"),
		highlightColor: asString(editor.getAttributes("highlight")["color"]),
		textBackground: asString(textStyle["backgroundColor"]),
		bold: editor.isActive("bold"),
		italic: editor.isActive("italic"),
		underline: editor.isActive("underline"),
		strike: editor.isActive("strike"),
		code: editor.isActive("code"),
		subscript: editor.isActive("subscript"),
		superscript: editor.isActive("superscript"),
		link: editor.isActive("link"),
		bulletList: editor.isActive(NODE_TYPES.bulletList),
		orderedList: editor.isActive(NODE_TYPES.orderedList),
		taskList: editor.isActive(NODE_TYPES.taskList),
		blockquote: editor.isActive(NODE_TYPES.blockquote),
		codeBlock: editor.isActive(NODE_TYPES.codeBlock),
		inTable: editor.isActive(NODE_TYPES.table),
		imageSelected: editor.isActive(NODE_TYPES.image),
		imageLayout: isImageLayout(editor.getAttributes(NODE_TYPES.image)["layout"]) ? editor.getAttributes(NODE_TYPES.image)["layout"] : null,
		canUndo: editor.can().undo(),
		canRedo: editor.can().redo()
	};
}
//#endregion
//#region src/ui/Toolbar.tsx
/**
* Highlighter colours.
*
* Pale on purpose: a highlight has to leave near-black text readable, which
* rules out the saturated versions of the same hues. The custom picker is
* there for anyone who disagrees.
*
* Nine, so that with "None" in front of them the grid is two even rows of
* five rather than two rows and an orphan.
*/
const HIGHLIGHT_SWATCHES = [
	{
		color: DEFAULT_HIGHLIGHT_COLOR,
		name: "Yellow"
	},
	{
		color: "#c8e6c9",
		name: "Green"
	},
	{
		color: "#b3e5fc",
		name: "Blue"
	},
	{
		color: "#f8bbd0",
		name: "Pink"
	},
	{
		color: "#ffe0b2",
		name: "Orange"
	},
	{
		color: "#e1bee7",
		name: "Purple"
	},
	{
		color: "#cfd8dc",
		name: "Grey"
	},
	{
		color: "#b2dfdb",
		name: "Teal"
	},
	{
		color: "#ffcdd2",
		name: "Red"
	}
];
/** Point sizes offered in the size picker — the set Word and Docs both ship. */
const FONT_SIZES = [
	8,
	9,
	10,
	11,
	12,
	14,
	16,
	18,
	20,
	24,
	28,
	32,
	36,
	48,
	60,
	72
];
const BLOCK_STYLE_OPTIONS = [{
	value: "p",
	label: "Normal text"
}, ...HEADING_LEVELS.map((level) => ({
	value: `h${level}`,
	label: `Heading ${level}`
}))];
const ALIGN_ICON = {
	left: "align-left",
	center: "align-center",
	right: "align-right",
	justify: "align-justify"
};
const ALIGN_LABEL$1 = {
	left: "Align left",
	center: "Align centre",
	right: "Align right",
	justify: "Justify"
};
/**
* The formatting toolbar.
*
* Lives in the package's main entry rather than behind the `./ui` sub-path:
* it needs a live `Editor`, so there is no version of it that avoids pulling
* ProseMirror in. The sub-path is reserved for chrome that genuinely stands
* alone.
*/
function Toolbar({ editor, language, onLanguageChange, dictation, pageSetup, onPageSetupChange, onImageFilesChosen, onImageReplace, fontProvider, onFontError }) {
	const state = useEditorState({
		editor,
		selector: ({ editor: instance }) => readToolbarState(instance)
	});
	const chain = () => editor.chain().focus();
	function setBlockStyle(value) {
		if (value === "p") {
			chain().setParagraph().run();
			return;
		}
		const level = Number.parseInt(value.slice(1), 10);
		const heading = HEADING_LEVELS.find((candidate) => candidate === level);
		if (heading) chain().setHeading({ level: heading }).run();
	}
	return /* @__PURE__ */ jsxs("div", {
		className: "burfy-docs-toolbar",
		role: "toolbar",
		"aria-label": "Formatting",
		children: [
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Undo",
				shortcut: "Ctrl+Z",
				disabled: !state.canUndo,
				onClick: () => chain().undo().run(),
				children: /* @__PURE__ */ jsx(Icon, { name: "undo" })
			}),
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Redo",
				shortcut: "Ctrl+Shift+Z",
				disabled: !state.canRedo,
				onClick: () => chain().redo().run(),
				children: /* @__PURE__ */ jsx(Icon, { name: "redo" })
			}),
			/* @__PURE__ */ jsx(ToolbarSeparator, {}),
			/* @__PURE__ */ jsx(ToolbarSelect, {
				label: "Paragraph style",
				value: state.blockStyle,
				options: BLOCK_STYLE_OPTIONS,
				onChange: setBlockStyle,
				width: 120
			}),
			fontProvider && /* @__PURE__ */ jsx(FontControls, {
				editor,
				provider: fontProvider,
				activeFamily: state.fontFamily,
				...onFontError ? { onError: onFontError } : {}
			}),
			/* @__PURE__ */ jsx(ToolbarSelect, {
				label: "Font size",
				value: state.fontSize ?? "11pt",
				options: FONT_SIZES.map((size) => ({
					value: `${size}pt`,
					label: String(size)
				})),
				onChange: (value) => chain().setFontSize(value).run(),
				width: 64
			}),
			/* @__PURE__ */ jsx(ToolbarSeparator, {}),
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Bold",
				shortcut: "Ctrl+B",
				active: state.bold,
				onClick: () => chain().toggleBold().run(),
				children: /* @__PURE__ */ jsx("strong", { children: "B" })
			}),
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Italic",
				shortcut: "Ctrl+I",
				active: state.italic,
				onClick: () => chain().toggleItalic().run(),
				children: /* @__PURE__ */ jsx("em", { children: "I" })
			}),
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Underline",
				shortcut: "Ctrl+U",
				active: state.underline,
				onClick: () => chain().toggleUnderline().run(),
				children: /* @__PURE__ */ jsx("span", {
					style: { textDecoration: "underline" },
					children: "U"
				})
			}),
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Strikethrough",
				active: state.strike,
				onClick: () => chain().toggleStrike().run(),
				children: /* @__PURE__ */ jsx("span", {
					style: { textDecoration: "line-through" },
					children: "S"
				})
			}),
			/* @__PURE__ */ jsx(ToolbarColor, {
				label: "Text colour",
				value: state.color ?? "#202124",
				onChange: (value) => chain().setColor(value).run(),
				children: "A"
			}),
			/* @__PURE__ */ jsx(ToolbarColorMenu, {
				label: "Highlight colour",
				value: state.highlight ? state.highlightColor ?? DEFAULT_HIGHLIGHT_COLOR : state.textBackground,
				swatches: HIGHLIGHT_SWATCHES,
				onChange: (color) => chain().unsetBackgroundColor().setHighlight({ color }).run(),
				onClear: () => chain().unsetHighlight().unsetBackgroundColor().run(),
				children: /* @__PURE__ */ jsx(Icon, { name: "highlighter" })
			}),
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Superscript",
				active: state.superscript,
				onClick: () => chain().toggleSuperscript().run(),
				children: "x²"
			}),
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Subscript",
				active: state.subscript,
				onClick: () => chain().toggleSubscript().run(),
				children: "x₂"
			}),
			/* @__PURE__ */ jsx(LinkControl, {
				editor,
				active: state.link
			}),
			/* @__PURE__ */ jsx(ToolbarSeparator, {}),
			TEXT_ALIGNMENTS.map((alignment) => /* @__PURE__ */ jsx(ToolbarButton, {
				label: ALIGN_LABEL$1[alignment],
				active: state.textAlign === alignment,
				onClick: () => chain().setTextAlign(alignment).run(),
				children: /* @__PURE__ */ jsx(Icon, { name: ALIGN_ICON[alignment] })
			}, alignment)),
			/* @__PURE__ */ jsx(ToolbarSelect, {
				label: "Line spacing",
				value: String(state.lineHeight ?? ""),
				options: [{
					value: "",
					label: "Spacing"
				}, ...LINE_HEIGHT_PRESETS.map((preset) => ({
					value: String(preset),
					label: String(preset)
				}))],
				onChange: (value) => chain().setLineHeight(value === "" ? null : Number.parseFloat(value)).run(),
				width: 96
			}),
			/* @__PURE__ */ jsx(ToolbarSeparator, {}),
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Bulleted list",
				active: state.bulletList,
				onClick: () => chain().toggleBulletList().run(),
				children: /* @__PURE__ */ jsx(Icon, { name: "list" })
			}),
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Numbered list",
				active: state.orderedList,
				onClick: () => chain().toggleOrderedList().run(),
				children: /* @__PURE__ */ jsx(Icon, { name: "list-ordered" })
			}),
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Checklist",
				active: state.taskList,
				onClick: () => chain().toggleTaskList().run(),
				children: /* @__PURE__ */ jsx(Icon, { name: "check-list" })
			}),
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Decrease indent",
				onClick: () => chain().outdent().run(),
				children: /* @__PURE__ */ jsx(Icon, { name: "indent-decrease" })
			}),
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Increase indent",
				onClick: () => chain().indent().run(),
				children: /* @__PURE__ */ jsx(Icon, { name: "indent-increase" })
			}),
			/* @__PURE__ */ jsx(ToolbarSeparator, {}),
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Quote",
				active: state.blockquote,
				onClick: () => chain().toggleBlockquote().run(),
				children: /* @__PURE__ */ jsx(Icon, { name: "quote" })
			}),
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Code block",
				active: state.codeBlock,
				onClick: () => chain().toggleCodeBlock().run(),
				children: /* @__PURE__ */ jsx(Icon, { name: "code" })
			}),
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Horizontal rule",
				onClick: () => chain().setHorizontalRule().run(),
				children: /* @__PURE__ */ jsx(Icon, { name: "separator" })
			}),
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Page break",
				shortcut: "Ctrl+Enter",
				onClick: () => chain().insertPageBreak().run(),
				children: /* @__PURE__ */ jsx(Icon, { name: "page-break" })
			}),
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Clear formatting",
				onClick: () => chain().unsetAllMarks().clearNodes().run(),
				children: /* @__PURE__ */ jsx(Icon, { name: "remove-format" })
			}),
			/* @__PURE__ */ jsx(TableControls, {
				editor,
				inTable: state.inTable
			}),
			pageSetup && onPageSetupChange && /* @__PURE__ */ jsx(PageBackgroundControl, {
				pageSetup,
				onPageSetupChange
			}),
			onImageFilesChosen && /* @__PURE__ */ jsx(ImageControls, {
				editor,
				imageSelected: state.imageSelected,
				activeLayout: state.imageLayout,
				onFilesChosen: onImageFilesChosen,
				...onImageReplace ? { onReplaceFile: onImageReplace } : {}
			}),
			language !== void 0 && onLanguageChange !== void 0 && /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx(ToolbarSeparator, {}), /* @__PURE__ */ jsx(LanguagePicker, {
				value: language,
				onChange: onLanguageChange
			})] }),
			dictation && language !== void 0 && /* @__PURE__ */ jsx(VoiceButton, {
				dictation,
				languageLabel: getLanguage(language).nativeLabel
			})
		]
	});
}
//#endregion
//#region src/pagination/PageChrome.tsx
/**
* The page illusion: one rectangle per page, painted behind the flow.
*
* Purely decorative and inert — it carries no content and takes no pointer
* events, so it can never come between the user and the text. Its positions
* come from the same geometry the layout engine used, which is why a page's
* edge always lands exactly where the engine decided to break.
*/
function PageChrome({ geometry, pageCount, pageGapPt, headerText, footerText, showPageNumbers = false, background, backgroundCss = null }) {
	const pageWidthPx = ptToCssPx(geometry.widthPt);
	const pageHeightPx = ptToCssPx(geometry.heightPt);
	const gapPx = ptToCssPx(pageGapPt);
	const backgroundOpacity = background?.kind === "image" ? background.opacity : 1;
	return /* @__PURE__ */ jsx("div", {
		className: "burfy-docs-page-layer",
		"aria-hidden": "true",
		children: Array.from({ length: Math.max(1, pageCount) }, (_, index) => /* @__PURE__ */ jsxs("div", {
			className: "burfy-docs-page-sheet",
			style: {
				top: `${index * (pageHeightPx + gapPx)}px`,
				width: `${pageWidthPx}px`,
				height: `${pageHeightPx}px`
			},
			children: [
				backgroundCss && /* @__PURE__ */ jsx("div", {
					className: "burfy-docs-page-background",
					"data-printed": isPrinted(background ?? { kind: "none" }) || void 0,
					style: {
						background: backgroundCss,
						opacity: backgroundOpacity
					}
				}),
				geometry.header.enabled && /* @__PURE__ */ jsx("div", {
					className: "burfy-docs-page-band burfy-docs-page-header",
					style: {
						top: `${ptToCssPx(geometry.margins.topPt)}px`,
						height: `${ptToCssPx(geometry.header.heightPt)}px`,
						left: `${ptToCssPx(geometry.margins.leftPt)}px`,
						right: `${ptToCssPx(geometry.margins.rightPt)}px`
					},
					children: headerText
				}),
				geometry.footer.enabled && /* @__PURE__ */ jsxs("div", {
					className: "burfy-docs-page-band burfy-docs-page-footer",
					style: {
						bottom: `${ptToCssPx(geometry.margins.bottomPt)}px`,
						height: `${ptToCssPx(geometry.footer.heightPt)}px`,
						left: `${ptToCssPx(geometry.margins.leftPt)}px`,
						right: `${ptToCssPx(geometry.margins.rightPt)}px`
					},
					children: [/* @__PURE__ */ jsx("span", { children: footerText }), showPageNumbers && /* @__PURE__ */ jsx("span", { children: index + 1 })]
				})
			]
		}, index))
	});
}
/** Total height of the page stack, including the gaps between pages. */
function pageStackHeightPx(geometry, pageCount, pageGapPt) {
	const pages = Math.max(1, pageCount);
	return ptToCssPx(pages * geometry.heightPt + (pages - 1) * pageGapPt);
}
//#endregion
//#region src/pagination/line-boxes.ts
/**
* Group text rects into lines.
*
* Rects are grouped by vertical overlap rather than by an exact `top`, because
* a line containing a superscript, a larger font, or an inline image produces
* several rects at different heights that all belong to the same line.
*/
function groupRectsIntoLines(rects) {
	const ordered = [...rects].filter((rect) => rect.bottom > rect.top).sort((a, b) => a.top - b.top || a.left - b.left);
	const lines = [];
	let currentBottom = Number.NEGATIVE_INFINITY;
	for (const rect of ordered) {
		if (rect.top < currentBottom) {
			const line = lines[lines.length - 1];
			if (line) {
				line.topPx = Math.min(line.topPx, rect.top);
				line.leftPx = Math.min(line.leftPx, rect.left);
			}
			currentBottom = Math.max(currentBottom, rect.bottom);
			continue;
		}
		lines.push({
			topPx: rect.top,
			leftPx: rect.left
		});
		currentBottom = rect.bottom;
	}
	return lines;
}
/**
* Convert boundary positions into the heights between them.
*
* Heights are measured boundary-to-boundary rather than from each element's
* own box, so collapsed margins and line leading are attributed to exactly one
* side and the parts always sum to the whole. Measuring boxes individually
* loses the collapsed margin, and a layout engine that loses a few points per
* block drifts by a whole line over a page.
*/
function heightsBetween(boundariesPx) {
	const heights = [];
	for (let i = 0; i + 1 < boundariesPx.length; i += 1) {
		const start = boundariesPx[i] ?? 0;
		const end = boundariesPx[i + 1] ?? start;
		heights.push(Math.max(0, end - start));
	}
	return heights;
}
//#endregion
//#region src/pagination/measure-flow.ts
/**
* Measuring the rendered document.
*
* The layout engine works in points and knows nothing about the DOM; this
* module is the only place the two meet. It runs with the spacer decorations
* **removed** — see `usePagination` — so every measurement reflects the
* document's natural layout rather than one already displaced by a previous
* pagination pass.
*/
/** Block types whose children are natural split points. */
const SPLIT_BY_CHILD = /* @__PURE__ */ new Set([
	NODE_TYPES.bulletList,
	NODE_TYPES.orderedList,
	NODE_TYPES.taskList
]);
/** Block types whose text line boxes are the split points. */
const SPLIT_BY_LINE = /* @__PURE__ */ new Set([NODE_TYPES.paragraph, NODE_TYPES.heading]);
/**
* Line measurements, keyed by the ProseMirror node itself.
*
* ProseMirror nodes are immutable and structurally shared, so an untouched
* paragraph is the *same object* from one transaction to the next and its
* entry stays valid automatically. A `WeakMap` means a deleted paragraph's
* entry is collected with it.
*
* This is not a micro-optimisation. Measuring every block on every keystroke
* costs ~60ms on a 36-page document — four times the frame budget, and
* plainly visible as stutter while typing.
*/
const lineCache = /* @__PURE__ */ new WeakMap();
/** Measure a block's line structure, reusing the cache when it still applies. */
function linesOf(child, node, rect) {
	const cached = lineCache.get(node);
	if (cached && Math.abs(cached.widthPx - rect.width) < .5) return cached;
	const lines = groupRectsIntoLines(textRects(child));
	const measured = {
		widthPx: rect.width,
		topOffsets: lines.map((line) => line.topPx - rect.top),
		leftOffsets: lines.map((line) => line.leftPx - rect.left)
	};
	lineCache.set(node, measured);
	return measured;
}
/**
* All text rects inside an element, ignoring widget decorations.
*
* Returns nothing where the environment performs no layout (jsdom, and any
* server-side render). The caller then treats the block as atomic, which is
* the honest answer: without layout there are no line boxes to split at.
*/
function textRects(element) {
	const range = document.createRange();
	if (typeof range.getClientRects !== "function") return [];
	const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
	const rects = [];
	for (let node = walker.nextNode(); node; node = walker.nextNode()) {
		if (!node.nodeValue) continue;
		range.selectNodeContents(node);
		for (const rect of range.getClientRects()) rects.push(rect);
	}
	return rects;
}
/**
* Measure the document into flow items.
*
* Heights are taken boundary-to-boundary — the top of one block to the top of
* the next — so collapsed margins land on exactly one side and the measured
* parts always add up to the whole rendered height.
*/
function measureFlow(view, scale = 1) {
	const container = view.dom;
	/**
	* `getBoundingClientRect` reports *rendered* pixels, so at any zoom other
	* than 100% every measurement comes back scaled. Undo it here rather than
	* downstream — the layout model is in points at 100% and must stay that way,
	* or the same document would paginate differently at different zoom levels.
	*/
	const toPt = (px) => cssPxToPt(px / scale);
	const children = Array.from(container.children);
	if (children.length === 0) return [];
	const containerBottom = container.getBoundingClientRect().bottom;
	const childRects = children.map((child) => child.getBoundingClientRect());
	const childTops = childRects.map((rect) => rect.top);
	const measured = [];
	/**
	* Set by an explicit page-break node so the *next* item starts a page. The
	* marker itself stays on the page it was typed on, which is where the user
	* put it — mirroring how Word shows a break mark at the foot of a page.
	*/
	let forceBreakNext = false;
	const positions = [];
	let position = 0;
	view.state.doc.forEach((node) => {
		positions.push(position);
		position += node.nodeSize;
	});
	for (let index = 0; index < children.length; index += 1) {
		const child = children[index];
		const node = view.state.doc.maybeChild(index);
		const pos = positions[index];
		if (!child || !node || pos === void 0) continue;
		const top = childTops[index] ?? 0;
		const bottom = childTops[index + 1] ?? containerBottom;
		const id = `${index}:${node.type.name}`;
		const keepWithNext = node.type.name === NODE_TYPES.heading;
		const breakBefore = forceBreakNext;
		forceBreakNext = node.type.name === NODE_TYPES.pageBreak;
		if (SPLIT_BY_LINE.has(node.type.name)) {
			const rect = childRects[index];
			const lines = rect ? linesOf(child, node, rect) : {
				topOffsets: [],
				leftOffsets: [],
				widthPx: 0
			};
			if (lines.topOffsets.length > 1) {
				const lineTops = lines.topOffsets.map((offset) => top + offset);
				const boundaries = [
					top,
					...lineTops.slice(1),
					bottom
				];
				const orphans = floatOrphans(child, lineTops);
				measured.push({
					pos,
					splits: lines.topOffsets.map((offset, lineIndex) => ({
						topPx: top + offset,
						leftPx: (rect?.left ?? 0) + (lines.leftOffsets[lineIndex] ?? 0)
					})),
					item: {
						kind: "lines",
						id,
						linesPt: heightsBetween(boundaries).map(toPt),
						...orphans === null ? {} : { orphans },
						...keepWithNext ? { keepWithNext } : {},
						...breakBefore ? { breakBefore } : {}
					}
				});
				continue;
			}
		} else if (SPLIT_BY_CHILD.has(node.type.name)) {
			const items = Array.from(child.children);
			if (items.length > 1) {
				const tops = items.map((element) => element.getBoundingClientRect().top);
				const boundaries = [
					top,
					...tops.slice(1),
					bottom
				];
				measured.push({
					pos,
					splits: tops.map((itemTop, itemIndex) => ({
						topPx: itemTop,
						leftPx: items[itemIndex]?.getBoundingClientRect().left ?? 0
					})),
					item: {
						kind: "lines",
						id,
						linesPt: heightsBetween(boundaries).map(toPt),
						...breakBefore ? { breakBefore } : {}
					}
				});
				continue;
			}
		}
		if (node.type.name === NODE_TYPES.table) {
			const measuredTable = measureTable(child, node, pos, top, bottom, toPt);
			if (measuredTable) {
				measured.push(breakBefore ? {
					...measuredTable,
					item: {
						...measuredTable.item,
						breakBefore
					}
				} : measuredTable);
				continue;
			}
		}
		measured.push({
			pos,
			splits: [],
			item: {
				kind: "block",
				id,
				heightPt: toPt(Math.max(0, bottom - top)),
				...keepWithNext ? { keepWithNext } : {},
				...breakBefore ? { breakBefore } : {}
			}
		});
	}
	return measured;
}
/**
* How many of a paragraph's lines a floated image reaches across.
*
* A float may not be cut in half by a page break, so every line beside it has
* to stay on the same page as the float itself. Expressing that as the
* paragraph's *orphan* count reuses the layout engine's existing rule —
* "leave at least this many lines together, or move the whole paragraph" —
* rather than inventing a second mechanism that would need its own tests and
* its own interactions with widows and `keepWithNext`.
*
* Returns `null` when there is no float to accommodate.
*/
function floatOrphans(element, lineTopsPx) {
	const floated = element.querySelector("[data-layout=\"wrapLeft\"], [data-layout=\"wrapRight\"]");
	if (!floated) return null;
	const floatBottom = floated.getBoundingClientRect().bottom;
	const linesBeside = lineTopsPx.filter((top) => top < floatBottom - 1).length;
	return Math.max(1, linesBeside);
}
/**
* Measure a table as a list of rows.
*
* Row boundaries are the only places a table may break, and they are real
* document positions — so they are computed from the node rather than probed
* from the rendered pixels.
*/
function measureTable(element, node, pos, top, bottom, toPt) {
	const rowElements = Array.from(element.querySelectorAll("tr"));
	if (rowElements.length < 2) return null;
	const rowPositions = [];
	let cursor = pos + 1;
	let headerRows = 0;
	let stillHeader = true;
	node.forEach((row) => {
		rowPositions.push(cursor);
		cursor += row.nodeSize;
		if (row.firstChild?.type.name === NODE_TYPES.tableHeader && stillHeader) headerRows += 1;
		else stillHeader = false;
	});
	const boundaries = [
		top,
		...rowElements.map((row) => row.getBoundingClientRect().top).slice(1),
		bottom
	];
	return {
		pos,
		splits: [],
		splitPositions: rowPositions,
		item: {
			kind: "tableRows",
			id: `${pos}:table`,
			rowsPt: heightsBetween(boundaries).map(toPt),
			headerRows,
			repeatHeader: headerRows > 0
		}
	};
}
/**
* Resolve a break into the document position where its spacer belongs.
*
* A split inside a paragraph has no structural position — line boxes are a
* rendering artefact, not part of the document — so it is resolved from the
* line's own coordinates. The probe is nudged a little inside the line
* because the exact top-left corner sits ambiguously between two lines.
*/
function resolveBreakPosition(view, measured, lineIndex) {
	if (lineIndex === null || lineIndex === 0) return measured.pos;
	const structural = measured.splitPositions?.[lineIndex];
	if (structural !== void 0) return structural;
	const split = measured.splits[lineIndex];
	if (!split) return null;
	const found = view.posAtCoords({
		left: split.leftPx + 1,
		top: split.topPx + 2
	});
	return found ? found.pos : null;
}
//#endregion
//#region src/pagination/usePagination.ts
/** `performance.measure` name for one full pagination pass. */
const PAGINATION_MEASURE = "burfy-docs:paginate";
/**
* Keeps the page breaks in step with the document.
*
* Each pass is: **clear the spacers, measure, paginate, re-apply**. Clearing
* first is what makes the measurement trustworthy — with the previous pass's
* spacers still in place, every block below the first break sits displaced,
* and the engine would be paginating its own output. All four steps happen
* inside one animation frame, so the browser paints once and the clear is
* never visible.
*/
function usePagination({ editor, geometry, pageGapPt, scale = 1, verticalAlign = "top", enabled = true }) {
	const [result, setResult] = useState(null);
	const frameRef = useRef(null);
	const measureAndApply = useCallback(() => {
		const view = editor?.view;
		if (!view || view.isDestroyed) return;
		const startedAt = performance.now();
		const clear = view.state.tr.setMeta(paginationPluginKey, { decorations: DecorationSet.empty }).setMeta("addToHistory", false);
		view.dispatch(clear);
		const measured = measureFlow(view, scale);
		const pagination = paginate({
			items: measured.map((entry) => entry.item),
			geometry,
			pageGapPt
		});
		/**
		* How far down each page's content sits inside its box.
		*
		* Applied here, to a pagination that has already been decided, and never
		* fed back into measurement — an alignment that could move a page break
		* would be a different and much worse feature.
		*/
		const offsetPxFor = (pageIndex) => {
			const page = pagination.pages[pageIndex];
			return page ? ptToCssPx(verticalOffsetPt(page, geometry, verticalAlign)) : 0;
		};
		const leading = offsetPxFor(0);
		const decorations = pagination.breaks.flatMap((pageBreak, breakIndex) => {
			const target = measured[pageBreak.itemIndex];
			if (!target) return [];
			const pos = resolveBreakPosition(view, target, pageBreak.lineIndex);
			if (pos === null) return [];
			const pageTopPx = ptToCssPx(pageBreak.nextPageTopPt) + offsetPxFor(breakIndex + 1);
			const gapPx = ptToCssPx(pageBreak.spacerHeightPt - pageBreak.nextPageTopPt);
			if (target.item.kind === "tableRows" && pageBreak.lineIndex !== null) {
				const table = view.nodeDOM(target.pos);
				const rows = table instanceof HTMLElement ? Array.from(table.querySelectorAll("tr")).filter((row) => !row.classList.contains("burfy-docs-repeated-header")) : [];
				const headerRows = target.item.repeatHeader ? rows.slice(0, target.item.headerRows) : [];
				return createTableBreakDecorations(pos, gapPx, pageTopPx, rows[0]?.children.length ?? 1, headerRows);
			}
			return [createSpacerDecoration(pos, gapPx), createPageTopDecoration(pos, pageTopPx)];
		});
		if (leading > 0) decorations.unshift(createPageTopDecoration(0, leading));
		view.dispatch(view.state.tr.setMeta(paginationPluginKey, { decorations: createDecorationSet(view.state.doc, decorations) }).setMeta("addToHistory", false));
		setResult(pagination);
		if (typeof performance.measure === "function") try {
			performance.measure(PAGINATION_MEASURE, { start: startedAt });
		} catch {}
	}, [
		editor,
		geometry,
		pageGapPt,
		scale,
		verticalAlign
	]);
	const schedule = useCallback(() => {
		if (frameRef.current !== null) return;
		frameRef.current = requestAnimationFrame(() => {
			frameRef.current = null;
			measureAndApply();
		});
	}, [measureAndApply]);
	useEffect(() => {
		if (!editor || !enabled) return;
		const onUpdate = ({ transaction }) => {
			if (transaction.getMeta(paginationPluginKey)) return;
			schedule();
		};
		editor.on("update", onUpdate);
		schedule();
		return () => {
			editor.off("update", onUpdate);
			if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
			frameRef.current = null;
		};
	}, [
		editor,
		enabled,
		schedule
	]);
	useEffect(() => {
		const element = editor?.view.dom;
		if (!element || !enabled || typeof ResizeObserver === "undefined") return;
		const observer = new ResizeObserver(() => schedule());
		observer.observe(element);
		return () => observer.disconnect();
	}, [
		editor,
		enabled,
		schedule
	]);
	/**
	* A font arriving changes the height of every line it touches.
	*
	* `ready` covers the initial load; `loadingdone` covers every font fetched
	* afterwards — which is the normal case, because a face is only downloaded
	* when something actually uses it. Without the event, choosing a font leaves
	* the page breaks computed for the fallback.
	*/
	useEffect(() => {
		if (!enabled || typeof document === "undefined" || !document.fonts) return;
		let cancelled = false;
		document.fonts.ready.then(() => {
			if (!cancelled) schedule();
		});
		const onLoaded = () => schedule();
		document.fonts.addEventListener("loadingdone", onLoaded);
		return () => {
			cancelled = true;
			document.fonts.removeEventListener("loadingdone", onLoaded);
		};
	}, [enabled, schedule]);
	return result;
}
//#endregion
//#region src/pagination/index.ts
/** Visual gap between pages on screen, in points. Zero when printing. */
const PAGE_GAP_PT = 18;
//#endregion
//#region src/ui/editor-menus.ts
const ALIGN_LABEL = {
	left: "Left",
	center: "Centre",
	right: "Right",
	justify: "Justified"
};
function createEditorMenus(editor, state, actions) {
	const chain = () => editor.chain().focus();
	return [
		{
			id: "file",
			label: "File",
			groups: [group("print", [{
				id: "print",
				label: "Print",
				shortcut: "Ctrl+P",
				run: actions.onPrint
			}])]
		},
		{
			id: "edit",
			label: "Edit",
			groups: [
				group("history", [{
					id: "undo",
					label: "Undo",
					shortcut: "Ctrl+Z",
					disabled: !state.canUndo,
					run: () => chain().undo().run()
				}, {
					id: "redo",
					label: "Redo",
					shortcut: "Ctrl+Shift+Z",
					disabled: !state.canRedo,
					run: () => chain().redo().run()
				}]),
				group("selection", [{
					id: "select-all",
					label: "Select all",
					shortcut: "Ctrl+A",
					run: () => chain().selectAll().run()
				}]),
				group("find", [{
					id: "find",
					label: "Find and replace",
					shortcut: "Ctrl+F",
					run: actions.onFind
				}])
			]
		},
		{
			id: "insert",
			label: "Insert",
			groups: [group("media", [actions.onInsertImage ? {
				id: "image",
				label: "Image…",
				run: actions.onInsertImage
			} : null, {
				id: "table",
				label: "Table",
				run: () => chain().insertTable({
					rows: 3,
					cols: 3,
					withHeaderRow: true
				}).run()
			}]), group("breaks", [{
				id: "horizontal-rule",
				label: "Horizontal line",
				run: () => chain().setHorizontalRule().run()
			}, {
				id: "page-break",
				label: "Page break",
				shortcut: "Ctrl+Enter",
				run: () => chain().insertPageBreak().run()
			}])]
		},
		{
			id: "format",
			label: "Format",
			groups: [
				group("marks", [
					{
						id: "bold",
						label: "Bold",
						shortcut: "Ctrl+B",
						checked: state.bold,
						run: () => chain().toggleBold().run()
					},
					{
						id: "italic",
						label: "Italic",
						shortcut: "Ctrl+I",
						checked: state.italic,
						run: () => chain().toggleItalic().run()
					},
					{
						id: "underline",
						label: "Underline",
						shortcut: "Ctrl+U",
						checked: state.underline,
						run: () => chain().toggleUnderline().run()
					},
					{
						id: "strike",
						label: "Strikethrough",
						checked: state.strike,
						run: () => chain().toggleStrike().run()
					}
				]),
				group("block-style", [{
					id: "paragraph",
					label: "Normal text",
					checked: state.blockStyle === "p",
					run: () => chain().setParagraph().run()
				}, ...HEADING_LEVELS.map((level) => ({
					id: `heading-${level}`,
					label: `Heading ${level}`,
					checked: state.blockStyle === `h${level}`,
					run: () => chain().setHeading({ level }).run()
				}))]),
				group("align", TEXT_ALIGNMENTS.map((alignment) => ({
					id: `align-${alignment}`,
					label: ALIGN_LABEL[alignment],
					checked: state.textAlign === alignment,
					run: () => chain().setTextAlign(alignment).run()
				}))),
				group("lists", [
					{
						id: "bullet-list",
						label: "Bulleted list",
						checked: state.bulletList,
						run: () => chain().toggleBulletList().run()
					},
					{
						id: "ordered-list",
						label: "Numbered list",
						checked: state.orderedList,
						run: () => chain().toggleOrderedList().run()
					},
					{
						id: "task-list",
						label: "Checklist",
						checked: state.taskList,
						run: () => chain().toggleTaskList().run()
					}
				]),
				group("blocks", [{
					id: "blockquote",
					label: "Quote",
					checked: state.blockquote,
					run: () => chain().toggleBlockquote().run()
				}, {
					id: "code-block",
					label: "Code block",
					checked: state.codeBlock,
					run: () => chain().toggleCodeBlock().run()
				}]),
				group("indent", [{
					id: "indent",
					label: "Increase indent",
					shortcut: "Ctrl+]",
					run: () => chain().indent().run()
				}, {
					id: "outdent",
					label: "Decrease indent",
					shortcut: "Ctrl+[",
					run: () => chain().outdent().run()
				}]),
				group("clear", [{
					id: "clear-formatting",
					label: "Clear formatting",
					run: () => chain().unsetAllMarks().clearNodes().run()
				}])
			]
		},
		{
			id: "table",
			label: "Table",
			groups: [
				group("insert", [{
					id: "insert-table",
					label: "Insert table",
					run: () => chain().insertTable({
						rows: 3,
						cols: 3,
						withHeaderRow: true
					}).run()
				}]),
				group("rows", [
					{
						id: "row-before",
						label: "Insert row above",
						disabled: !state.inTable,
						run: () => chain().addRowBefore().run()
					},
					{
						id: "row-after",
						label: "Insert row below",
						disabled: !state.inTable,
						run: () => chain().addRowAfter().run()
					},
					{
						id: "delete-row",
						label: "Delete row",
						disabled: !state.inTable,
						run: () => chain().deleteRow().run()
					}
				]),
				group("columns", [
					{
						id: "column-before",
						label: "Insert column left",
						disabled: !state.inTable,
						run: () => chain().addColumnBefore().run()
					},
					{
						id: "column-after",
						label: "Insert column right",
						disabled: !state.inTable,
						run: () => chain().addColumnAfter().run()
					},
					{
						id: "delete-column",
						label: "Delete column",
						disabled: !state.inTable,
						run: () => chain().deleteColumn().run()
					}
				]),
				group("table", [
					{
						id: "header-row",
						label: "Toggle header row",
						disabled: !state.inTable,
						run: () => chain().toggleHeaderRow().run()
					},
					{
						id: "merge-split",
						label: "Merge or split cells",
						disabled: !state.inTable,
						run: () => chain().mergeOrSplit().run()
					},
					{
						id: "delete-table",
						label: "Delete table",
						disabled: !state.inTable,
						run: () => chain().deleteTable().run()
					}
				])
			]
		},
		{
			id: "help",
			label: "Help",
			groups: [group("help", [{
				id: "shortcuts",
				label: "Keyboard shortcuts",
				run: actions.onShortcuts
			}])]
		}
	];
}
//#endregion
//#region src/ui/EditorMenuBar.tsx
/**
* The menu bar, bound to a live editor.
*
* Separate from `DocsEditor` because the editor is null until TipTap has
* mounted, and the state hook this needs cannot be called conditionally.
* Reading through `useEditorState` also means the menus re-render when
* something they *show* changes — the caret entering a table, a mark turning
* on — and not on every keystroke.
*/
function EditorMenuBar({ editor, actions, extras }) {
	const state = useEditorState({
		editor,
		selector: ({ editor: instance }) => readToolbarState(instance)
	});
	const menus = mergeMenus(createEditorMenus(editor, state, actions), extras ?? {});
	return /* @__PURE__ */ jsx(MenuBar, { menus });
}
//#endregion
//#region src/ime/CandidatePopup.tsx
/**
* The candidate list, anchored under the caret.
*
* Positioned `fixed` from the caret's own viewport coordinates so it follows
* the text through scrolling and zoom without needing to know anything about
* the page stack it is floating over.
*
* It never steals focus: `mousedown` is prevented, because losing the
* selection would destroy the very range the choice is about to replace.
*/
function CandidatePopup({ state, onAccept, onHighlight }) {
	return /* @__PURE__ */ jsxs("div", {
		className: "burfy-docs-ime-popup",
		style: {
			left: `${state.anchor.leftPx}px`,
			top: `${state.anchor.topPx + 4}px`
		},
		role: "listbox",
		"aria-label": `Transliteration suggestions for ${state.roman}`,
		onMouseDown: (event) => event.preventDefault(),
		children: [state.candidates.map((candidate, index) => /* @__PURE__ */ jsxs("button", {
			type: "button",
			role: "option",
			"aria-selected": index === state.highlight,
			className: "burfy-docs-ime-option",
			"data-active": index === state.highlight || void 0,
			onMouseEnter: () => onHighlight(index),
			onClick: () => onAccept(index),
			children: [
				index < 9 && /* @__PURE__ */ jsx("span", {
					className: "burfy-docs-ime-index",
					children: index + 1
				}),
				/* @__PURE__ */ jsx("span", {
					className: "burfy-docs-ime-text",
					children: candidate
				}),
				candidate === state.roman && /* @__PURE__ */ jsx("span", {
					className: "burfy-docs-ime-hint",
					children: "as typed"
				})
			]
		}, candidate)), state.pending && state.candidates.length <= 1 && /* @__PURE__ */ jsx("span", {
			className: "burfy-docs-ime-pending",
			children: "Looking up…"
		})]
	});
}
//#endregion
//#region src/search/FindReplacePanel.tsx
/**
* The find-and-replace bar.
*
* Sits above the page rather than floating over it: a panel covering the text
* you are searching is a poor way to show you what it found.
*/
function FindReplacePanel({ search, onClose }) {
	const [replacement, setReplacement] = useState("");
	const input = useRef(null);
	useEffect(() => {
		input.current?.focus();
		input.current?.select();
	}, []);
	const total = search.matches.length;
	const position = search.activeIndex >= 0 ? search.activeIndex + 1 : 0;
	return /* @__PURE__ */ jsxs("div", {
		className: "burfy-docs-find",
		role: "search",
		"aria-label": "Find and replace",
		onKeyDown: (event) => {
			if (event.key === "Escape") {
				event.preventDefault();
				onClose();
			}
		},
		children: [
			/* @__PURE__ */ jsx("input", {
				ref: input,
				className: "burfy-docs-tb-input",
				type: "text",
				placeholder: "Find",
				"aria-label": "Find",
				value: search.query,
				onChange: (event) => search.setQuery(event.target.value),
				onKeyDown: (event) => {
					if (event.key !== "Enter") return;
					event.preventDefault();
					if (event.shiftKey) search.previous();
					else search.next();
				}
			}),
			/* @__PURE__ */ jsx("span", {
				className: "burfy-docs-find-count",
				role: "status",
				"aria-label": "Search results",
				"aria-live": "polite",
				"aria-atomic": "true",
				children: search.query === "" ? "" : total === 0 ? "No results" : `${position} of ${total}`
			}),
			/* @__PURE__ */ jsx("button", {
				type: "button",
				className: "burfy-docs-tb-button",
				"aria-label": "Previous match",
				disabled: total === 0,
				onClick: search.previous,
				children: "↑"
			}),
			/* @__PURE__ */ jsx("button", {
				type: "button",
				className: "burfy-docs-tb-button",
				"aria-label": "Next match",
				disabled: total === 0,
				onClick: search.next,
				children: "↓"
			}),
			/* @__PURE__ */ jsx("input", {
				className: "burfy-docs-tb-input",
				type: "text",
				placeholder: "Replace with",
				"aria-label": "Replace with",
				value: replacement,
				onChange: (event) => setReplacement(event.target.value)
			}),
			/* @__PURE__ */ jsx("button", {
				type: "button",
				className: "burfy-docs-tb-button",
				disabled: total === 0,
				onClick: () => search.replaceCurrent(replacement),
				children: "Replace"
			}),
			/* @__PURE__ */ jsx("button", {
				type: "button",
				className: "burfy-docs-tb-button",
				disabled: total === 0,
				onClick: () => search.replaceAll(replacement),
				children: "Replace all"
			}),
			/* @__PURE__ */ jsxs("label", {
				className: "burfy-docs-radio",
				children: [/* @__PURE__ */ jsx("input", {
					type: "checkbox",
					checked: search.options.caseSensitive === true,
					onChange: (event) => search.setOptions({
						...search.options,
						caseSensitive: event.target.checked
					})
				}), /* @__PURE__ */ jsx("span", { children: "Match case" })]
			}),
			/* @__PURE__ */ jsxs("label", {
				className: "burfy-docs-radio",
				children: [/* @__PURE__ */ jsx("input", {
					type: "checkbox",
					checked: search.options.wholeWord === true,
					onChange: (event) => search.setOptions({
						...search.options,
						wholeWord: event.target.checked
					})
				}), /* @__PURE__ */ jsx("span", { children: "Whole words" })]
			}),
			/* @__PURE__ */ jsx("span", { className: "burfy-docs-dialog-spacer" }),
			/* @__PURE__ */ jsx("button", {
				type: "button",
				className: "burfy-docs-tb-button",
				"aria-label": "Close find and replace",
				onClick: onClose,
				children: "✕"
			})
		]
	});
}
//#endregion
//#region src/pagination/usePageBackground.ts
/**
* The CSS for a page background, resolving an `asset:` image if there is one.
*
* Colours and gradients need nothing asynchronous and are returned on the
* first render. Only an image waits, and until it arrives the page is simply
* unpainted rather than flashing a placeholder.
*/
function usePageBackground(background, storeOverride) {
	const contextStore = useAssetStore();
	const store = storeOverride === void 0 ? contextStore : storeOverride;
	const [resolved, setResolved] = useState(null);
	const assetId = background?.kind === "image" ? parseAssetUri(background.src) : null;
	useEffect(() => {
		setResolved(null);
		if (!assetId || !store) return;
		let cancelled = false;
		/**
		* Whether the store actually handed out a reference.
		*
		* The store counts references on `resolveUrl`, so releasing one it never
		* granted — which is what an unconditional cleanup does when the resolve
		* has not settled, and StrictMode's double-invoke guarantees that — leaves
		* the count one too high and the object URL alive for the session.
		*/
		let held = false;
		store.resolveUrl(assetId).then((url) => {
			held = url !== null;
			if (cancelled) {
				if (held) store.release(assetId);
				return;
			}
			setResolved(url);
		}).catch(() => {
			if (!cancelled) setResolved(null);
		});
		return () => {
			cancelled = true;
			if (held) store.release(assetId);
		};
	}, [assetId, store]);
	if (!background) return null;
	return pageBackgroundCss(background, resolved);
}
//#endregion
//#region src/ui/fit-scale.ts
/**
* Fitting the page to the space there is.
*
* A phone viewport is 390px and an A4 page is 794px, so at 100% the reader
* sees the left half of the sheet and scrolls sideways for the rest. Scaling
* the page down is safe in a way that reflowing it would not be: zoom here is
* a CSS transform and the layout engine divides it back out, so the page
* breaks on a phone are the same breaks as on a desktop and the same ones that
* print. What you see stays what you get, only smaller.
*/
/** Never magnify. Fitting is for when there is too little room, not too much. */
const MAX_FIT = 1;
/**
* How much the page must shrink to fit `availableWidth`.
*
* Returns 1 when it already fits, or when the width is not yet known — a
* measurement of zero arrives on the first frame and before layout, and
* scaling to zero would blank the document.
*/
function fitScale(availableWidth, pageWidthPx) {
	if (!Number.isFinite(availableWidth) || availableWidth <= 0) return MAX_FIT;
	if (!Number.isFinite(pageWidthPx) || pageWidthPx <= 0) return MAX_FIT;
	return Math.min(MAX_FIT, availableWidth / pageWidthPx);
}
/**
* The scale actually applied, combining the user's zoom with the fit.
*
* The user's zoom still multiplies: asking for 150% on a phone means 150% of
* whatever fits, which overflows deliberately, because that is what zooming in
* is for.
*/
function effectiveZoom(zoom, availableWidth, pageWidthPx, enabled) {
	if (!enabled) return zoom;
	return zoom * fitScale(availableWidth, pageWidthPx);
}
//#endregion
//#region src/ui/useFitScale.ts
/**
* Settle time before a new width is acted on.
*
* The width feeds the layout engine, and a full `measureFlow` on a long
* document is tens of milliseconds — the repo's own note puts a 36-page
* document at around 60ms. Dragging a window edge fires a resize every frame,
* so without this the editor would re-paginate ~60 times a second for the
* length of the drag. Nothing before this change re-paginated on resize at
* all, so this is a cost the fit introduced and has to pay for.
*/
const SETTLE_MS = 120;
/**
* The content width of an element, tracked as it changes.
*
* `contentRect` throughout: it excludes padding, which the page cannot use
* anyway and which itself changes with the viewport. Mixing measures here —
* a border-box first frame and a content-box thereafter — makes the first
* paint fit against a width tens of pixels too large.
*/
function useContentWidth() {
	const ref = useRef(null);
	const [width, setWidth] = useState(0);
	useEffect(() => {
		const element = ref.current;
		if (!element) return;
		/** Content box, by hand — what `contentRect` reports, without an observer. */
		const contentWidth = () => {
			const style = getComputedStyle(element);
			const padding = Number.parseFloat(style.paddingLeft || "0") + Number.parseFloat(style.paddingRight || "0");
			return Math.max(0, element.clientWidth - padding);
		};
		if (typeof ResizeObserver === "undefined") {
			setWidth(contentWidth());
			return;
		}
		let timer = null;
		let settled = 0;
		const observer = new ResizeObserver((entries) => {
			const next = entries[0]?.contentRect.width;
			if (next === void 0 || next === settled) return;
			if (timer) clearTimeout(timer);
			timer = setTimeout(() => {
				timer = null;
				settled = next;
				setWidth(next);
			}, SETTLE_MS);
		});
		observer.observe(element);
		return () => {
			if (timer) clearTimeout(timer);
			observer.disconnect();
		};
	}, []);
	return [ref, width];
}
//#endregion
//#region src/ime/editor-run.ts
/**
* Read the current text block as a string whose offsets line up with document
* positions.
*
* `textBetween` with a placeholder for leaf nodes is what keeps the alignment:
* `textContent` silently omits them, and every offset after an image would be
* wrong by one.
*/
function blockText($from) {
	return $from.parent.textBetween(0, $from.parent.content.size, "\n", "￼");
}
/** The roman word the caret is in, if any. */
function currentRomanRun(editor, language) {
	const { selection } = editor.state;
	if (!selection.empty) return null;
	const $from = selection.$from;
	if (!$from.parent.isTextblock) return null;
	if ($from.parent.type.spec.code) return null;
	const run = findRomanRun(blockText($from), $from.parentOffset, language);
	if (!run) return null;
	const blockStart = $from.start();
	return {
		roman: run.text,
		expected: run.text,
		from: blockStart + run.start,
		to: blockStart + run.end
	};
}
/**
* The already-converted word the caret is in, if it still carries its source.
*
* This is what `Ctrl/Cmd+G` re-opens. The range is found by walking outward
* from the caret while the same mark instance continues, so a sentence of
* separately converted words does not collapse into one range.
*/
function markedRunAt(editor) {
	const { selection, schema } = editor.state;
	const markType = schema.marks[MARK_TYPES.translitSource];
	if (!markType) return null;
	const $from = selection.$from;
	const mark = $from.marks().find((candidate) => candidate.type === markType) ?? $from.nodeBefore?.marks.find((candidate) => candidate.type === markType);
	if (!mark) return null;
	const parentStart = $from.start();
	const parentEnd = $from.end();
	let from = $from.pos;
	let to = $from.pos;
	editor.state.doc.nodesBetween(parentStart, parentEnd, (node, pos) => {
		if (!node.isText) return;
		if (!node.marks.some((candidate) => candidate === mark)) return;
		const nodeEnd = pos + node.nodeSize;
		if (pos <= $from.pos && $from.pos <= nodeEnd) {
			from = Math.min(from, pos);
			to = Math.max(to, nodeEnd);
		}
	});
	if (to <= from) return null;
	const roman = typeof mark.attrs["roman"] === "string" ? mark.attrs["roman"] : null;
	if (!roman) return null;
	return {
		roman,
		expected: editor.state.doc.textBetween(from, to),
		from,
		to,
		reopened: true,
		lang: typeof mark.attrs["lang"] === "string" ? mark.attrs["lang"] : null
	};
}
/** Insert plain text at a position without disturbing the caret's logical spot. */
function insertSeparator(editor, at, text) {
	if (!text) return true;
	const tr = editor.state.tr;
	tr.setMeta(IME_COMMIT_META, true);
	tr.insertText(text, at);
	editor.view.dispatch(tr);
	return true;
}
/**
* Replace a range with the chosen candidate.
*
* The candidate carries the `translitSource` mark; the trailing separator does
* not, so a space typed after a converted word never inherits the source of
* the word before it. The transaction is flagged so the mark-clearing plugin
* does not immediately strip what we just wrote.
*/
function commitCandidate(editor, options) {
	const { range, candidate, trailing, lang, preserveSelection = false } = options;
	const { state } = editor;
	/**
	* The range may be stale.
	*
	* A commit can be deferred while an outstanding lookup finishes, and the
	* user keeps typing in the meantime. Replacing the range blindly would
	* overwrite whatever has since taken its place, so its content is checked
	* before it is touched.
	*/
	const size = state.doc.content.size;
	if (range.from < 0 || range.to > size || range.to <= range.from) return false;
	if (state.doc.textBetween(range.from, range.to) !== range.expected) return false;
	const markType = state.schema.marks[MARK_TYPES.translitSource];
	if (!markType) return false;
	const nodes = [state.schema.text(candidate, [markType.create({
		roman: range.roman,
		lang
	})])];
	if (trailing) nodes.push(state.schema.text(trailing));
	const tr = state.tr;
	tr.setMeta(IME_COMMIT_META, true);
	tr.replaceWith(range.from, range.to, nodes);
	if (preserveSelection) tr.setSelection(state.selection.map(tr.doc, tr.mapping));
	else tr.setSelection(TextSelection.create(tr.doc, tr.mapping.map(range.to)));
	editor.view.dispatch(tr);
	return true;
}
/**
* Where to put the popup, or `null` if that cannot be worked out.
*
* `coordsAtPos` throws for a position the view has not rendered — and in any
* environment without layout. Losing the whole IME because the popup has
* nowhere to sit would be a poor trade.
*/
function caretCoords(view, pos) {
	try {
		const coords = view.coordsAtPos(pos);
		return {
			leftPx: coords.left,
			topPx: coords.bottom
		};
	} catch {
		return null;
	}
}
/**
* The transliteration IME.
*
* Three sources feed one ranked list: what this user has chosen before, what
* is already cached, and what the service eventually returns. The first two
* are synchronous, so the popup appears on the same frame as the keystroke and
* only *gains* candidates when the network answers — it never blanks out
* waiting, and it never blocks typing.
*/
function useTransliteration({ editor, language, enabled = true, learning, debounceMs = 150, fetchImpl }) {
	const [state, setState] = useState(null);
	const languageDef = useMemo(() => getLanguage(language), [language]);
	const active = enabled && languageDef.transliterable;
	/** Candidate lists from the service, so re-typing a word costs nothing. */
	const responseCache = useRef(new LruCache());
	/** Learned counts, mirrored in memory so ranking never awaits storage. */
	const learnedCache = useRef(new LruCache());
	/** The range the current candidates belong to. */
	const rangeRef = useRef(null);
	/** A run the user dismissed with Escape; do not re-open it unprompted. */
	const dismissedRef = useRef(null);
	const debounceRef = useRef(null);
	/**
	* Close the popup.
	*
	* Deliberately does **not** abort any outstanding request. Accepting a word
	* closes the popup while its lookup may still be in flight, and cancelling
	* it there would hand the user back their own roman text. Requests are
	* per-word, de-duplicated and time-limited, so letting one finish unattended
	* costs nothing — and a late response is ignored by the range check anyway.
	*/
	const clear = useCallback(() => {
		rangeRef.current = null;
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = null;
		setState(null);
	}, []);
	/** Build the ranked list from whatever is already known, with no awaiting. */
	const rankNow = useCallback((roman) => {
		const remote = responseCache.current.get(candidateKey(languageDef.code, roman));
		const learned = learnedCache.current.get(learningKey(languageDef.code, roman));
		return rankCandidates({
			remote: remote ?? [],
			learned,
			literal: roman
		});
	}, [languageDef]);
	/**
	* Get this word's candidates, from the cache or the service.
	*
	* In-flight requests are shared: the debounce timer and an impatient
	* keystroke both await the same promise rather than racing two requests for
	* the same word.
	*/
	const inFlight = useRef(/* @__PURE__ */ new Map());
	const resolveCandidates = useCallback((roman) => {
		const key = candidateKey(languageDef.code, roman);
		const cached = responseCache.current.get(key);
		if (cached) return Promise.resolve(cached);
		const existing = inFlight.current.get(key);
		if (existing) return existing;
		const controller = new AbortController();
		const timeout = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);
		const request = transliterate(roman, languageDef.code, {
			signal: controller.signal,
			...fetchImpl ? { fetchImpl } : {}
		}).then((result) => {
			responseCache.current.set(key, result.candidates);
			return result.candidates;
		}).catch(() => []).finally(() => {
			clearTimeout(timeout);
			inFlight.current.delete(key);
		});
		inFlight.current.set(key, request);
		return request;
	}, [languageDef, fetchImpl]);
	const show = useCallback((range, options = {}) => {
		const view = editor?.view;
		if (!view) return;
		const key = `${range.from}:${range.roman}`;
		if (!options.force && dismissedRef.current === key) return;
		if (!options.force) dismissedRef.current = null;
		rangeRef.current = range;
		const cached = responseCache.current.has(candidateKey(languageDef.code, range.roman));
		setState({
			roman: range.roman,
			candidates: rankNow(range.roman),
			highlight: 0,
			anchor: caretCoords(view, range.to),
			pending: !cached
		});
		const lKey = learningKey(languageDef.code, range.roman);
		if (learning && !learnedCache.current.has(lKey)) learning.get(lKey).then((entry) => {
			learnedCache.current.set(lKey, entry ?? {});
			if (rangeRef.current?.roman !== range.roman) return;
			setState((current) => current ? {
				...current,
				candidates: rankNow(range.roman)
			} : current);
		});
		if (cached) return;
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => {
			resolveCandidates(range.roman).then(() => {
				if (rangeRef.current?.roman !== range.roman) return;
				setState((current) => current ? {
					...current,
					candidates: rankNow(range.roman),
					pending: false
				} : current);
			});
		}, debounceMs);
	}, [
		editor,
		languageDef,
		learning,
		debounceMs,
		fetchImpl,
		rankNow
	]);
	/** Recompute after any selection or document change. */
	const refresh = useCallback(() => {
		if (!editor || !active) {
			clear();
			return;
		}
		const run = currentRomanRun(editor, languageDef);
		if (!run) {
			clear();
			return;
		}
		show(run);
	}, [
		editor,
		active,
		languageDef,
		clear,
		show
	]);
	const accept = useCallback((index, trailing = "") => {
		const range = rangeRef.current;
		if (!editor || !range || !state) return;
		const separator = range.reopened ? "" : trailing;
		const commit = (candidates, options = {}) => {
			const candidate = candidates[index] ?? candidates[0];
			if (!candidate) return;
			if (!commitCandidate(editor, {
				range,
				candidate,
				trailing: options.deferred ? "" : separator,
				lang: languageDef.code,
				preserveSelection: options.deferred === true
			})) return;
			if (learning && candidate !== range.roman) {
				const lKey = learningKey(languageDef.code, range.roman);
				learnedCache.current.set(lKey, recordChoice(learnedCache.current.get(lKey), candidate));
				learning.record(lKey, candidate);
			}
		};
		/**
		* Accepting before the lookup has returned.
		*
		* The only thing on offer at that moment is the roman text itself, so
		* committing it would hand a fast typist their own input back. Instead
		* the keystroke is consumed, the outstanding request is awaited, and the
		* candidate the user actually meant is committed a moment later.
		*/
		if (state.pending && state.candidates.length <= 1) {
			if (debounceRef.current) clearTimeout(debounceRef.current);
			debounceRef.current = null;
			insertSeparator(editor, range.to, separator);
			const roman = range.roman;
			resolveCandidates(roman).then(() => commit(rankNow(roman), { deferred: true }));
			clear();
			return;
		}
		commit(state.candidates);
		clear();
	}, [
		editor,
		state,
		languageDef,
		learning,
		clear,
		rankNow,
		resolveCandidates
	]);
	const dismiss = useCallback(() => {
		const range = rangeRef.current;
		if (range) dismissedRef.current = `${range.from}:${range.roman}`;
		clear();
	}, [clear]);
	const setHighlight = useCallback((index) => {
		setState((current) => current ? {
			...current,
			highlight: index
		} : current);
	}, []);
	/** Re-open candidates on an already-converted word. */
	const reopen = useCallback(() => {
		if (!editor || !active) return false;
		const marked = markedRunAt(editor);
		if (!marked) return false;
		dismissedRef.current = null;
		show({
			roman: marked.roman,
			expected: marked.expected,
			from: marked.from,
			to: marked.to,
			reopened: true
		}, { force: true });
		return true;
	}, [
		editor,
		active,
		show
	]);
	const handlerRef = useRef(() => false);
	useEffect(() => {
		handlerRef.current = (event) => {
			const intent = resolveImeKey({
				key: event.key,
				shiftKey: event.shiftKey,
				ctrlKey: event.ctrlKey,
				metaKey: event.metaKey,
				altKey: event.altKey,
				isComposing: event.isComposing
			}, state !== null && state.candidates.length > 0);
			switch (intent.kind) {
				case "accept":
					accept(state?.highlight ?? 0, intent.trailing);
					return true;
				case "accept-index":
					if (!state || intent.index >= state.candidates.length) return false;
					accept(intent.index, " ");
					return true;
				case "dismiss":
					dismiss();
					return true;
				case "move":
					setHighlight(moveHighlight(state?.highlight ?? 0, intent.delta, state?.candidates.length ?? 0));
					return true;
				case "reopen": return reopen();
				default: return false;
			}
		};
	}, [
		state,
		accept,
		dismiss,
		setHighlight,
		reopen
	]);
	useEffect(() => {
		if (!editor) return;
		editor.on("selectionUpdate", refresh);
		editor.on("update", refresh);
		return () => {
			editor.off("selectionUpdate", refresh);
			editor.off("update", refresh);
		};
	}, [editor, refresh]);
	useEffect(() => {
		if (!active) clear();
	}, [active, clear]);
	useEffect(() => () => clear(), [clear]);
	return {
		state,
		accept,
		dismiss,
		setHighlight,
		handleKeyDown: useCallback((event) => handlerRef.current(event), [])
	};
}
//#endregion
//#region src/voice/insert-dictation.ts
/**
* The character immediately before the caret, or `null` when there is none.
*
* This is what decides whether a dictated word needs a leading space and
* whether it starts a sentence. A selection is measured from its *start*: the
* insertion replaces it, so the new text ends up following whatever preceded
* the selection, not what preceded the caret.
*/
function precedingChar(editor) {
	const { selection, doc } = editor.state;
	const from = selection.from;
	if (from <= selection.$from.start()) return null;
	const text = doc.textBetween(from - 1, from);
	return text.length > 0 ? text : null;
}
/**
* Apply the plan.
*
* Text goes in as a text node, never through `insertContent`'s string form,
* which parses its argument as HTML — dictating "less than b greater than"
* must produce those characters, not a bold tag.
*/
function insertDictation(editor, steps) {
	if (steps.length === 0 || editor.isDestroyed) return false;
	const { state } = editor;
	const tr = state.tr;
	const hardBreak = state.schema.nodes[NODE_TYPES.hardBreak];
	for (const step of steps) {
		if (step.kind === "text") {
			if (step.text) tr.insertText(step.text);
			continue;
		}
		if (step.kind === "newline") {
			if (hardBreak) {
				tr.replaceSelectionWith(hardBreak.create());
				continue;
			}
		}
		const pos = tr.selection.from;
		if (canSplit(tr.doc, pos)) tr.split(pos);
	}
	if (!tr.docChanged) return false;
	tr.scrollIntoView();
	try {
		editor.view.dispatch(tr);
	} catch {}
	return true;
}
//#endregion
//#region src/voice/messages.ts
function speechErrorMessage(error) {
	switch (error.code) {
		case "not-allowed": return "Microphone access was blocked. Allow it in your browser’s site settings, then try again.";
		case "audio-capture": return "No microphone was found. Check that one is connected and not in use by another app.";
		case "network": return "The speech service could not be reached. What you have dictated so far is safe.";
		case "unsupported": return "Voice typing is not supported in this browser. Chrome, Edge and Safari support it.";
		case "no-speech":
		case "aborted": return null;
		case "unknown": return `Voice typing stopped: ${error.message}`;
	}
}
/** Why the microphone is not on offer — for a tooltip, when it is worth one. */
function unavailableMessage(reason) {
	switch (reason) {
		case "disabled": return "Voice typing is switched off.";
		case "unavailable": return "The configured speech engine cannot run in this browser.";
		case "none": return "Voice typing is not supported in this browser. Chrome, Edge and Safari support it.";
	}
}
//#endregion
//#region src/voice/useDictation.ts
/**
* How long a session may hear nothing at all before it gives up.
*
* Engines end a turn after a few seconds of silence, and a session that
* survives a pause is the behaviour people expect — nobody dictates without
* stopping to think. So a turn ending is not the end of anything; only a
* genuinely long silence is, and then the user is told, because a microphone
* that switched itself off without a word is indistinguishable from one that
* has stopped working.
*/
const SILENCE_LIMIT_MS = 6e4;
/**
* Floor on the gap between restarts of a turn that heard nothing.
*
* A turn that ends without a word — a wedged engine, a device that
* disappeared — would otherwise spin `start()` as fast as the event loop
* allows. A turn that *did* hear something restarts immediately, whatever its
* length, because the gap between turns is exactly where the next word gets
* lost, and that is the complaint this whole path exists to answer.
*/
const MIN_RESTART_GAP_MS = 300;
/**
* Consecutive failures, with nothing heard in between, before we believe them.
*
* A restarted recogniser reports `network` often enough that treating the
* first one as fatal ends sessions that were about to carry on perfectly well.
* A service that is genuinely unreachable fails every time, so it is caught on
* the third — and until then the session simply retries.
*/
const MAX_CONSECUTIVE_FAILURES = 3;
/**
* A dictation session.
*
* The microphone never owns the document: interim text is a decoration, only
* final results become transactions, and every path out of a session —
* stopping, erroring, switching language, unmounting — releases the engine.
*
* Note what is deliberately *not* React state: the interim guess. It changes
* ten times a second, nothing renders it but a DOM node the plugin owns, and
* holding it in state re-rendered the entire editor on every syllable.
*/
function useDictation({ editor, language, preference = "auto", engines, localeOverrides, onError }) {
	const [status, setStatus] = useState("idle");
	const selection = useMemo(() => selectSpeechEngine(preference, engines ?? []), [preference, engines]);
	const engine = selection.engine;
	const locale = useMemo(() => speechLocale(language, localeOverrides), [language, localeOverrides]);
	const recognizer = useRef(null);
	/** True while the *user* wants to be dictating — not whether a turn is live. */
	const wanted = useRef(false);
	/** When a chunk last arrived; the silence budget is measured from here. */
	const lastHeardAt = useRef(0);
	const lastStartAt = useRef(0);
	/** Did this turn hear anything at all? Decides how eagerly it is restarted. */
	const heardThisTurn = useRef(false);
	/** The guess on screen, still uncommitted by the engine. */
	const pendingGuess = useRef("");
	/** Set when the *user* ended the session, so a pending guess is not lost. */
	const forcedStop = useRef(false);
	/** Failures since anything was last heard; a blip is not an outage. */
	const failuresSinceHeard = useRef(0);
	const restartTimer = useRef(null);
	const editorRef = useRef(editor);
	const languageRef = useRef(language);
	const localeRef = useRef(locale);
	const onErrorRef = useRef(onError);
	useEffect(() => {
		editorRef.current = editor;
		languageRef.current = language;
		localeRef.current = locale;
		onErrorRef.current = onError;
	}, [
		editor,
		language,
		locale,
		onError
	]);
	const showInterim = useCallback((text) => {
		const view = editorRef.current?.view;
		if (view) setInterimText(view, text);
	}, []);
	const clearRestart = useCallback(() => {
		if (restartTimer.current) clearTimeout(restartTimer.current);
		restartTimer.current = null;
	}, []);
	const beginTurn = useCallback(() => {
		if (!wanted.current) return;
		lastStartAt.current = Date.now();
		heardThisTurn.current = false;
		forcedStop.current = false;
		recognizer.current?.start({
			locale: localeRef.current,
			interim: true,
			continuous: true
		});
	}, []);
	const commit = useCallback((transcript) => {
		const instance = editorRef.current;
		if (!instance || instance.isDestroyed) return;
		const steps = planInsertion(transcript, languageRef.current, { precedingChar: precedingChar(instance) });
		showInterim("");
		insertDictation(instance, steps);
		if (!instance.isFocused) instance.view.focus();
	}, [showInterim]);
	const endSession = useCallback(() => {
		wanted.current = false;
		pendingGuess.current = "";
		clearRestart();
		recognizer.current?.abort();
		recognizer.current = null;
		showInterim("");
		setStatus("idle");
	}, [showInterim, clearRestart]);
	/** Build the recognizer once per engine; the handlers read through refs. */
	const ensureRecognizer = useCallback(() => {
		if (recognizer.current) return recognizer.current;
		if (!engine) return null;
		const created = engine.create({
			onStart: () => {
				if (wanted.current) setStatus("listening");
			},
			onChunk: (chunk) => {
				lastHeardAt.current = Date.now();
				heardThisTurn.current = true;
				failuresSinceHeard.current = 0;
				if (chunk.isFinal) {
					pendingGuess.current = "";
					commit(chunk.transcript);
					return;
				}
				pendingGuess.current = chunk.transcript;
				showInterim(chunk.transcript);
			},
			onError: (error) => {
				if (error.code === "no-speech" || error.code === "aborted") return;
				if (error.code === "network") {
					failuresSinceHeard.current += 1;
					if (failuresSinceHeard.current < MAX_CONSECUTIVE_FAILURES) return;
				}
				const message = speechErrorMessage(error);
				if (message) onErrorRef.current?.(message);
				endSession();
			},
			onEnd: () => {
				if (pendingGuess.current && forcedStop.current) {
					const orphaned = pendingGuess.current;
					pendingGuess.current = "";
					commit(orphaned);
				}
				forcedStop.current = false;
				if (!wanted.current) {
					setStatus("idle");
					return;
				}
				if (Date.now() - lastHeardAt.current >= SILENCE_LIMIT_MS) {
					endSession();
					onErrorRef.current?.("Voice typing stopped — nothing was heard for a minute. Click the microphone to start again.");
					return;
				}
				clearRestart();
				const sinceStart = Date.now() - lastStartAt.current;
				if (heardThisTurn.current || sinceStart >= MIN_RESTART_GAP_MS) {
					beginTurn();
					return;
				}
				restartTimer.current = setTimeout(beginTurn, MIN_RESTART_GAP_MS - sinceStart);
			}
		});
		recognizer.current = created;
		return created;
	}, [
		engine,
		commit,
		showInterim,
		endSession,
		beginTurn,
		clearRestart
	]);
	const start = useCallback(() => {
		const instance = editorRef.current;
		if (!engine || !instance || wanted.current) return;
		if (!ensureRecognizer()) return;
		if (!instance.isFocused && !instance.isDestroyed) instance.view.focus();
		wanted.current = true;
		lastHeardAt.current = Date.now();
		failuresSinceHeard.current = 0;
		setStatus("starting");
		beginTurn();
	}, [
		engine,
		ensureRecognizer,
		beginTurn
	]);
	/** Finish the turn and keep what was heard — unlike `endSession`'s abort. */
	const stop = useCallback(() => {
		if (!wanted.current) return;
		wanted.current = false;
		clearRestart();
		setStatus("idle");
		forcedStop.current = true;
		recognizer.current?.stop();
	}, [clearRestart]);
	const toggle = useCallback(() => {
		if (wanted.current) stop();
		else start();
	}, [start, stop]);
	const handleKeyDown = useCallback((event) => {
		if (!engine) return false;
		if (!(event.metaKey || event.ctrlKey) || !event.shiftKey) return false;
		if (event.key.toLowerCase() !== "s") return false;
		event.preventDefault();
		toggle();
		return true;
	}, [engine, toggle]);
	useEffect(() => {
		if (!wanted.current) return;
		beginTurn();
	}, [locale, beginTurn]);
	/**
	* Release the microphone on unmount, and whenever the engine changes.
	*
	* A recognizer belongs to its engine: keeping one across a swap would leave
	* the old engine holding an open microphone with nobody listening to it.
	*/
	useEffect(() => () => {
		wanted.current = false;
		pendingGuess.current = "";
		if (restartTimer.current) clearTimeout(restartTimer.current);
		restartTimer.current = null;
		recognizer.current?.abort();
		recognizer.current = null;
		setStatus("idle");
	}, [engine]);
	return {
		supported: engine !== null,
		unavailableReason: "reason" in selection ? selection.reason : null,
		engineLabel: engine?.label ?? null,
		audioDestination: engine?.audioDestination ?? null,
		status,
		listening: status !== "idle",
		locale,
		start,
		stop,
		toggle,
		handleKeyDown
	};
}
//#endregion
//#region src/DocsEditor.tsx
/** Stable empty list, so `useDictation` is not handed a new array each render. */
const NO_ENGINES = [];
/** Is this the "paste without formatting" chord? */
function isPlainPasteChord(event) {
	return (event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === "v";
}
/**
* The Burfy Docs editing surface.
*
* **One ProseMirror view**, with the pages painted around it. Splitting the
* document into an editor per page is the obvious implementation and a trap:
* selection, undo, find and IME composition all break at the boundary. Instead
* the flow is continuous and spacer decorations push content onto the next
* page, with an inert layer behind drawing the sheets themselves. Both come
* from one `PaginationResult`, so they cannot disagree.
*/
function DocsEditor({ content, onChange, pageSetup = DEFAULT_PAGE_SETUP, defaultStyle = DEFAULT_BLOCK_STYLE, zoom = 1, fitToWidth = true, onRenderZoomChange, placeholder, readOnly = false, showToolbar = true, headerText, footerText, onPageCountChange, language = "en", onLanguageChange, learning, transliterationFetch, voice, onVoiceError, assetStore, onImageError, fontProvider, showOutline = true, showRuler = true, onPageSetupChange, showMenuBar = true, menuExtras }) {
	/**
	* Geometry depends on the page's *shape*, not on what is painted behind it.
	*
	* Built from the shape alone, rather than from the whole `PageSetup`,
	* because a new setup object makes a new geometry — which re-runs
	* `measureFlow`, `paginate` and two ProseMirror dispatches. Dragging the
	* background's strength slider produced one of those per frame: a full
	* repagination to change a colour nothing is measured against.
	*/
	const { size, orientation, margins, header, footer } = pageSetup;
	const geometry = useMemo(() => createPageGeometry({
		size,
		orientation,
		margins,
		header,
		footer
	}), [
		size,
		orientation,
		margins,
		header,
		footer
	]);
	const extensions = useMemo(() => createExtensions(placeholder === void 0 ? {} : { placeholder }), [placeholder]);
	/**
	* Set while the plain-paste chord is the most recent keystroke. Reset on
	* every other key so an unconsumed chord cannot silently strip formatting
	* from a later, ordinary paste.
	*/
	const plainPasteArmed = useRef(false);
	/**
	* The IME's key handler.
	*
	* Held in a ref because `useEditor` runs before the IME hook (which needs
	* the editor), so `editorProps` cannot close over it directly.
	*/
	const imeKeyDown = useRef(() => false);
	/** The microphone's shortcut handler, held for the same reason. */
	const voiceKeyDown = useRef(() => false);
	/**
	* Import handler for dropped and pasted files.
	*
	* A ref for the same reason as the IME's: `editorProps` is captured when the
	* editor is created, before the geometry and store this needs are in scope.
	*/
	const importFiles = useRef(() => void 0);
	/** Opens find-and-replace from inside the editor's key handler. */
	const openFind = useRef(() => void 0);
	const editor = useEditor({
		extensions,
		content,
		editable: !readOnly,
		onUpdate: ({ editor: instance }) => onChange?.(instance.getJSON()),
		editorProps: {
			attributes: {
				class: "burfy-docs-prose",
				role: "textbox",
				"aria-multiline": "true",
				"aria-label": "Document body"
			},
			transformPastedHTML: sanitizePastedHtml,
			handleKeyDown: (_view, event) => {
				if (imeKeyDown.current(event)) return true;
				if (voiceKeyDown.current(event)) return true;
				if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "f") {
					event.preventDefault();
					openFind.current();
					return true;
				}
				plainPasteArmed.current = isPlainPasteChord(event);
				return false;
			},
			handlePaste: (view, event) => {
				const pastedFiles = Array.from(event.clipboardData?.files ?? []).filter(isProbablyImage);
				if (pastedFiles.length > 0) {
					importFiles.current(pastedFiles);
					return true;
				}
				if (!plainPasteArmed.current) return false;
				plainPasteArmed.current = false;
				const text = event.clipboardData?.getData("text/plain");
				if (!text) return false;
				view.dispatch(view.state.tr.insertText(text));
				return true;
			},
			handleDrop: (_view, event) => {
				const dropped = Array.from(event.dataTransfer?.files ?? []).filter(isProbablyImage);
				if (dropped.length === 0) return false;
				event.preventDefault();
				importFiles.current(dropped);
				return true;
			}
		}
	}, [extensions, readOnly]);
	/** The page's own width, before any scaling — what the fit is measured against. */
	const pageWidthPx = ptToCssPx(geometry.widthPt);
	/**
	* Resolved once, here, for both the screen and the printer.
	*
	* `PageChrome` used to resolve it for itself, which left printing with only
	* the model — and an image background is an `asset:` reference that has to
	* be turned into a URL before anything can paint it.
	*/
	const backgroundCss = usePageBackground(pageSetup.background, assetStore ?? null);
	/** Only a background marked printed reaches the printer. */
	const printedBackgroundCss = pageSetup.background && isPrinted(pageSetup.background) ? backgroundCss : null;
	const [canvasRef, canvasWidth] = useContentWidth();
	/**
	* What is actually rendered, once the room available is taken into account.
	*
	* Everything downstream uses this rather than the `zoom` prop — the layout
	* engine included, since it divides the scale back out of its measurements.
	* Passing the unscaled value to one and the scaled value to the other is how
	* the screen and the print output start disagreeing.
	*/
	const renderZoom = effectiveZoom(zoom, canvasWidth, pageWidthPx, fitToWidth);
	useEffect(() => {
		onRenderZoomChange?.(renderZoom);
	}, [renderZoom, onRenderZoomChange]);
	const pagination = usePagination({
		editor,
		geometry,
		pageGapPt: 18,
		scale: renderZoom,
		verticalAlign: pageSetup.verticalAlign ?? "top"
	});
	const dictation = useDictation({
		editor,
		language,
		preference: voice?.preference ?? "auto",
		engines: voice?.engines ?? NO_ENGINES,
		localeOverrides: voice?.localeOverrides,
		onError: onVoiceError
	});
	const ime = useTransliteration({
		editor,
		language,
		learning,
		enabled: !dictation.listening,
		...transliterationFetch ? { fetchImpl: transliterationFetch } : {}
	});
	useEffect(() => {
		imeKeyDown.current = ime.handleKeyDown;
	}, [ime.handleKeyDown]);
	useEffect(() => {
		voiceKeyDown.current = dictation.handleKeyDown;
	}, [dictation.handleKeyDown]);
	/** Swap the picture inside the selected image, keeping layout and alt text. */
	const handleImageReplace = useCallback((file) => {
		if (!editor || !assetStore) return;
		replaceSelectedImage(editor, assetStore, file, { maxWidthPx: ptToCssPx(geometry.contentWidthPt) }).then((message) => {
			if (message) onImageError?.(message);
		});
	}, [
		editor,
		assetStore,
		geometry,
		onImageError
	]);
	const handleImageFiles = useCallback((files) => {
		if (!editor || !assetStore || files.length === 0) return;
		insertImageFiles(editor, assetStore, files, { maxWidthPx: ptToCssPx(geometry.contentWidthPt) }).then((result) => {
			for (const message of result.errors) onImageError?.(message);
		});
	}, [
		editor,
		assetStore,
		geometry,
		onImageError
	]);
	useEffect(() => {
		importFiles.current = handleImageFiles;
	}, [handleImageFiles]);
	/**
	* The file picker behind Insert → Image.
	*
	* A menu item cannot open one on its own — only a real `<input type="file">`
	* can, and only from a user gesture — so the menu clicks this.
	*/
	const imageInput = useRef(null);
	const [findOpen, setFindOpen] = useState(false);
	const [shortcutsOpen, setShortcutsOpen] = useState(false);
	const search = useSearch(editor, findOpen);
	useEffect(() => {
		openFind.current = () => setFindOpen(true);
	}, []);
	/**
	* Outline and counts, recomputed when the document changes.
	*
	* Derived from the editor's own document rather than the `content` prop:
	* that prop is the *initial* value and never updates, so anything reading it
	* would show the document as it was when opened.
	*/
	const [summary, setSummary] = useState({
		outline: [],
		words: 0,
		characters: 0
	});
	useEffect(() => {
		if (!editor) return;
		const recompute = () => {
			const outline = [];
			editor.state.doc.forEach((node, _offset, index) => {
				if (node.type.name !== "heading") return;
				const text = node.textContent.trim();
				if (text === "") return;
				const level = typeof node.attrs["level"] === "number" ? node.attrs["level"] : 1;
				outline.push({
					level,
					text,
					blockIndex: index
				});
			});
			const counts = countText(editor.state.doc.textBetween(0, editor.state.doc.content.size, "\n"));
			setSummary({
				outline,
				words: counts.words,
				characters: counts.characters
			});
		};
		recompute();
		editor.on("update", recompute);
		return () => {
			editor.off("update", recompute);
		};
	}, [editor]);
	/**
	* Indents of the paragraph the caret is in.
	*
	* Read from the editor on every selection change rather than from the
	* document prop: the ruler's markers have to follow the caret, and the prop
	* is the document as it was when opened.
	*/
	const [indents, setIndents] = useState({
		leftPt: 0,
		rightPt: 0,
		firstLinePt: 0
	});
	useEffect(() => {
		if (!editor) return;
		const read = () => {
			const attrs = editor.isActive("heading") ? editor.getAttributes("heading") : editor.getAttributes("paragraph");
			const number = (value) => typeof value === "number" && Number.isFinite(value) ? value : 0;
			setIndents({
				leftPt: number(attrs["indentPt"]),
				rightPt: number(attrs["indentRightPt"]),
				firstLinePt: number(attrs["firstLineIndentPt"])
			});
		};
		read();
		editor.on("selectionUpdate", read);
		editor.on("update", read);
		return () => {
			editor.off("selectionUpdate", read);
			editor.off("update", read);
		};
	}, [editor]);
	const handleIndentsChange = useCallback((next) => {
		editor?.chain().focus().setIndents({
			...next.leftPt === void 0 ? {} : { leftPt: next.leftPt || null },
			...next.rightPt === void 0 ? {} : { rightPt: next.rightPt || null },
			...next.firstLinePt === void 0 ? {} : { firstLinePt: next.firstLinePt || null }
		}).run();
	}, [editor]);
	const handleMarginChange = useCallback((margins) => {
		onPageSetupChange?.({
			...pageSetup,
			margins: {
				...pageSetup.margins,
				...margins
			}
		});
	}, [pageSetup, onPageSetupChange]);
	const goToOutlineEntry = useCallback((entry) => {
		if (!editor) return;
		let position = 0;
		editor.state.doc.forEach((_node, offset, index) => {
			if (index === entry.blockIndex) position = offset;
		});
		editor.chain().focus().setTextSelection(position + 1).scrollIntoView().run();
	}, [editor]);
	const pageCount = pagination?.pages.length ?? 1;
	useEffect(() => {
		onPageCountChange?.(pageCount);
	}, [pageCount, onPageCountChange]);
	/**
	* Publish the page geometry to the document root as CSS variables, so
	* `@page` always reflects this document.
	*
	* It has to live here rather than in the print button's handler: the browser
	* has its own print shortcut, and a user pressing Cmd+P would otherwise get
	* whatever paper size the browser defaults to instead of the one the
	* document was laid out for. `@page` can only read variables from `:root`,
	* which is why this touches the host document at all.
	*/
	useEffect(() => {
		applyPrintGeometry(window.document.documentElement, geometry);
	}, [geometry]);
	const stackHeightPx = pageStackHeightPx(geometry, pageCount, 18);
	return /* @__PURE__ */ jsx(AssetStoreProvider, {
		store: assetStore ?? null,
		children: /* @__PURE__ */ jsxs("div", {
			className: "burfy-docs-root",
			children: [
				showMenuBar && editor && /* @__PURE__ */ jsx(EditorMenuBar, {
					editor,
					extras: menuExtras,
					actions: {
						onPrint: () => printDocument(geometry, { background: {
							css: printedBackgroundCss,
							...pageSetup.background?.kind === "image" ? { opacity: pageSetup.background.opacity } : {}
						} }),
						onFind: () => setFindOpen(true),
						onShortcuts: () => setShortcutsOpen(true),
						...assetStore ? { onInsertImage: () => imageInput.current?.click() } : {}
					}
				}),
				assetStore && /* @__PURE__ */ jsx("input", {
					ref: imageInput,
					type: "file",
					"aria-label": "Image file to insert from the menu",
					accept: "image/png,image/jpeg,image/gif,image/webp,image/svg+xml",
					multiple: true,
					hidden: true,
					onChange: (event) => {
						handleImageFiles(Array.from(event.target.files ?? []));
						event.target.value = "";
					}
				}),
				showToolbar && editor && /* @__PURE__ */ jsx(Toolbar, {
					editor,
					language,
					...onLanguageChange ? { onLanguageChange } : {},
					...voice ? { dictation } : {},
					...onPageSetupChange ? {
						pageSetup,
						onPageSetupChange
					} : {},
					...assetStore ? {
						onImageFilesChosen: handleImageFiles,
						onImageReplace: handleImageReplace
					} : {},
					...fontProvider ? { fontProvider } : {},
					...onImageError ? { onFontError: onImageError } : {}
				}),
				findOpen && /* @__PURE__ */ jsx(FindReplacePanel, {
					search,
					onClose: () => setFindOpen(false)
				}),
				/* @__PURE__ */ jsxs("div", {
					className: "burfy-docs-workspace",
					children: [showOutline && /* @__PURE__ */ jsx(OutlinePane, {
						outline: summary.outline,
						onSelect: goToOutlineEntry
					}), /* @__PURE__ */ jsxs("div", {
						className: "burfy-docs-page-column",
						children: [showRuler && /* @__PURE__ */ jsx(Ruler, {
							geometry,
							zoom: renderZoom,
							indents,
							...onPageSetupChange ? { onPageSetupChange: handleMarginChange } : {},
							onIndentsChange: handleIndentsChange
						}), /* @__PURE__ */ jsx("div", {
							className: "burfy-docs-canvas",
							ref: canvasRef,
							children: /* @__PURE__ */ jsx("div", {
								className: "burfy-docs-zoom",
								style: {
									width: `${pageWidthPx * renderZoom}px`,
									height: `${stackHeightPx * renderZoom}px`
								},
								children: /* @__PURE__ */ jsxs("div", {
									className: "burfy-docs-stack",
									style: {
										width: `${pageWidthPx}px`,
										height: `${stackHeightPx}px`,
										transform: renderZoom === 1 ? void 0 : `scale(${renderZoom})`
									},
									children: [/* @__PURE__ */ jsx(PageChrome, {
										geometry,
										pageCount,
										pageGapPt: 18,
										background: pageSetup.background,
										backgroundCss,
										showPageNumbers: true,
										...headerText === void 0 ? {} : { headerText },
										...footerText === void 0 ? {} : { footerText }
									}), /* @__PURE__ */ jsx("div", {
										className: "burfy-docs-flow",
										style: {
											top: `${ptToCssPx(geometry.contentOriginPt.yPt)}px`,
											left: `${ptToCssPx(geometry.margins.leftPt)}px`,
											width: `${ptToCssPx(geometry.contentWidthPt)}px`,
											fontFamily: defaultStyle.fontFamily,
											fontSize: `${defaultStyle.fontSizePt}pt`,
											lineHeight: defaultStyle.lineHeight,
											color: defaultStyle.color
										},
										children: /* @__PURE__ */ jsx(EditorContent, { editor })
									})]
								})
							})
						})]
					})]
				}),
				/* @__PURE__ */ jsxs("div", {
					className: "burfy-docs-status",
					"aria-label": "Document statistics",
					children: [
						/* @__PURE__ */ jsxs("span", { children: [
							summary.words,
							" ",
							summary.words === 1 ? "word" : "words"
						] }),
						/* @__PURE__ */ jsxs("span", { children: [summary.characters, " characters"] }),
						/* @__PURE__ */ jsxs("span", { children: [
							pageCount,
							" ",
							pageCount === 1 ? "page" : "pages"
						] }),
						/* @__PURE__ */ jsx("span", { className: "burfy-docs-dialog-spacer" }),
						/* @__PURE__ */ jsx("button", {
							type: "button",
							className: "burfy-docs-status-button",
							onClick: () => setShortcutsOpen(true),
							children: "Keyboard shortcuts"
						})
					]
				}),
				shortcutsOpen && /* @__PURE__ */ jsx(ShortcutSheet, { onClose: () => setShortcutsOpen(false) }),
				ime.state && ime.state.anchor && ime.state.candidates.length > 0 && /* @__PURE__ */ jsx(CandidatePopup, {
					state: {
						...ime.state,
						anchor: ime.state.anchor
					},
					onAccept: (index) => ime.accept(index),
					onHighlight: ime.setHighlight
				})
			]
		})
	});
}
//#endregion
//#region src/document.ts
/**
* Adapt a core document tree to TipTap's `JSONContent`.
*
* The two describe the same JSON, but the core type spells its optional
* fields `T | undefined` (so the Zod schemas can be typed against it) while
* TipTap's omits the `undefined`. Under `exactOptionalPropertyTypes` that is
* a genuine mismatch, so the conversion is funnelled through this one
* function rather than scattering casts across the package.
*/
function toEditorContent(node) {
	return node;
}
/**
* Adapt TipTap's `JSONContent` back to the core document tree.
*
* TipTap types `type` as optional because `JSONContent` also describes partial
* fragments used when inserting content; a document produced by
* `editor.getJSON()` always has one. The value is validated against the schema
* whenever it is read back from storage, which is where untrusted input would
* actually arrive.
*/
function toDocumentNode(content) {
	return content;
}
/**
* An empty document body — a single empty paragraph.
*
* Delegates to `@burfy/docs-core` so the editor and the exporters cannot
* disagree about what "empty" means.
*/
function createEmptyDocument() {
	return toEditorContent(createEmptyContent());
}
//#endregion
//#region src/voice/web-speech-engine.ts
/** The recognizer constructor this browser offers, if it offers one. */
function findSpeechRecognition(scope = typeof window === "undefined" ? void 0 : window) {
	return scope?.SpeechRecognition ?? scope?.webkitSpeechRecognition ?? null;
}
/**
* The engine's own error vocabulary, narrowed to ours.
*
* Everything unrecognised becomes `unknown` rather than being passed through:
* the UI has a sentence for each code we define, and none for a string it has
* never seen.
*/
function toSpeechError(event) {
	return {
		code: event.error === "not-allowed" || event.error === "service-not-allowed" ? "not-allowed" : event.error === "no-speech" || event.error === "audio-capture" || event.error === "network" || event.error === "aborted" ? event.error : "unknown",
		message: event.message ?? `Speech recognition failed (${event.error})`
	};
}
var WebSpeechRecognizer = class {
	engine = "webspeech";
	#recognition = null;
	/** Set by `abort()`: nothing that arrives afterwards is anyone's business. */
	#discarded = false;
	#create;
	#handlers;
	constructor(create, handlers) {
		this.#create = create;
		this.#handlers = handlers;
	}
	start(config) {
		this.#stopInternal();
		this.#discarded = false;
		const recognition = this.#create();
		recognition.lang = config.locale;
		recognition.continuous = config.continuous;
		recognition.interimResults = config.interim;
		recognition.maxAlternatives = 1;
		recognition.onstart = () => {
			if (this.#discarded) return;
			this.#handlers.onStart();
		};
		recognition.onresult = (event) => {
			if (this.#discarded) return;
			this.#emit(event);
		};
		recognition.onerror = (event) => {
			if (this.#discarded) return;
			this.#handlers.onError(toSpeechError(event));
		};
		recognition.onend = () => {
			this.#recognition = null;
			const finished = recognition;
			queueMicrotask(() => {
				finished.onstart = null;
				finished.onresult = null;
				finished.onerror = null;
				finished.onend = null;
			});
			if (this.#discarded) return;
			this.#handlers.onEnd();
		};
		this.#recognition = recognition;
		try {
			recognition.start();
		} catch {
			this.#recognition = null;
			recognition.onstart = null;
			recognition.onresult = null;
			recognition.onerror = null;
			recognition.onend = null;
			this.#handlers.onEnd();
		}
	}
	/**
	* Turn one event into chunks.
	*
	* Finals are emitted individually — each becomes its own undo step. Interim
	* results are concatenated into a single guess, and one is emitted even when
	* it is empty, because that is the signal to clear the ghost text left over
	* from the guess before.
	*/
	#emit(event) {
		let interim = "";
		for (let index = event.resultIndex; index < event.results.length; index += 1) {
			const result = event.results[index];
			const alternative = result?.[0];
			if (!result || !alternative) continue;
			if (result.isFinal) this.#handlers.onChunk({
				transcript: alternative.transcript,
				isFinal: true,
				...typeof alternative.confidence === "number" ? { confidence: alternative.confidence } : {}
			});
			else interim += alternative.transcript;
		}
		this.#handlers.onChunk({
			transcript: interim,
			isFinal: false
		});
	}
	stop() {
		this.#recognition?.stop();
	}
	abort() {
		this.#discarded = true;
		this.#stopInternal();
	}
	#stopInternal() {
		const recognition = this.#recognition;
		if (!recognition) return;
		this.#recognition = null;
		recognition.onstart = null;
		recognition.onresult = null;
		recognition.onerror = null;
		recognition.onend = null;
		recognition.abort();
	}
};
function createWebSpeechEngine(options = {}) {
	const resolve = () => options.constructorImpl ?? findSpeechRecognition();
	return {
		id: "webspeech",
		label: "Browser speech recognition",
		audioDestination: "your browser's speech service",
		isAvailable: () => resolve() !== null,
		create: (handlers) => new WebSpeechRecognizer(() => {
			const Recognition = resolve();
			if (!Recognition) throw new Error("Speech recognition is not available");
			return new Recognition();
		}, handlers)
	};
}
//#endregion
//#region src/voice/cloud-engine.ts
/**
* A cloud speech service, behind the same port.
*
* It records with `MediaRecorder` and posts audio to an endpoint of ours,
* which is the half of a cloud engine whose design would otherwise be
* guesswork later. Until `endpoint` is configured it reports itself
* unavailable, so `auto` never picks it and the switch simply has nothing to
* choose — the backend is a Phase 9b deliverable.
*
* Shipping the client now is what makes the eventual ramp a configuration
* change rather than a release.
*/
/**
* What the endpoint must answer with.
*
* Validated, not trusted: a response that does not match yields no text and no
* exception, exactly like a failed transliteration lookup.
*/
const CloudResponse = z.object({
	transcript: z.string(),
	isFinal: z.boolean().default(true),
	confidence: z.number().min(0).max(1).optional()
});
function defaultGetUserMedia(constraints) {
	return navigator.mediaDevices.getUserMedia(constraints);
}
var CloudRecognizer = class {
	engine = "cloud";
	#recorder = null;
	#stream = null;
	#discarded = false;
	/** Guards against ending the session twice — `stop()` then an error, say. */
	#ended = false;
	/**
	* Which turn is current.
	*
	* `getUserMedia` is asynchronous, so a `start()` that supersedes an earlier
	* one — a language change while the microphone is still opening — can have
	* the *older* permission resolve afterwards and install its stream on top.
	* The result is two live recorders: audio uploaded twice, and a microphone
	* `abort()` has no handle on. Each turn carries a token and a late arrival
	* releases its own stream instead.
	*/
	#turn = 0;
	/** Segment timer: each chunk is recorded, stopped and sent complete. */
	#segmentTimer = null;
	/** False once the session is winding down; the last segment still flushes. */
	#recording = false;
	#locale = "";
	#options;
	#handlers;
	constructor(options, handlers) {
		this.#options = options;
		this.#handlers = handlers;
	}
	start(config) {
		this.#teardown();
		this.#discarded = false;
		this.#ended = false;
		this.#locale = config.locale;
		const turn = ++this.#turn;
		this.#options.getUserMedia({ audio: true }).then((stream) => {
			if (this.#discarded || turn !== this.#turn) {
				releaseStream(stream);
				return;
			}
			this.#stream = stream;
			this.#recordSegment(turn);
			this.#handlers.onStart();
		}).catch((error) => {
			if (this.#discarded || turn !== this.#turn) return;
			this.#handlers.onError({
				code: isPermissionError(error) ? "not-allowed" : "audio-capture",
				message: error instanceof Error ? error.message : "The microphone could not be opened"
			});
			this.#finish();
		});
	}
	/**
	* Record one complete segment, then start the next.
	*
	* Deliberately *not* `recorder.start(chunkMs)`. That emits a timesliced
	* stream in which only the first blob carries the WebM container header, so
	* every chunk after the first is undecodable on its own — and each one is
	* POSTed as a standalone file. Dictation would have died a few seconds in,
	* through the network-failure counter, with the service rejecting audio it
	* could not parse.
	*
	* Stopping and restarting per segment costs a small gap at each boundary.
	* That is the honest cost of framing audio as a series of files, and it is
	* one more reason this adapter wants replacing with a socket that streams
	* (see §4 of the design doc) rather than tuning.
	*/
	#recordSegment(turn) {
		const stream = this.#stream;
		if (!stream || this.#discarded || turn !== this.#turn) return;
		const recorder = this.#options.recorderFactory(stream);
		this.#recorder = recorder;
		this.#recording = true;
		recorder.ondataavailable = (event) => {
			if (this.#discarded || turn !== this.#turn || event.data.size === 0) return;
			this.#send(event.data, this.#locale);
		};
		recorder.onstop = () => {
			if (this.#discarded || turn !== this.#turn) return;
			if (this.#recording) this.#recordSegment(turn);
			else this.#finish();
		};
		recorder.start();
		this.#clearSegmentTimer();
		this.#segmentTimer = setTimeout(() => {
			this.#segmentTimer = null;
			if (this.#discarded || turn !== this.#turn) return;
			if (recorder.state !== "inactive") recorder.stop();
		}, this.#options.chunkMs);
	}
	#clearSegmentTimer() {
		if (this.#segmentTimer) clearTimeout(this.#segmentTimer);
		this.#segmentTimer = null;
	}
	async #send(audio, locale) {
		try {
			const url = new URL(this.#options.endpoint, globalThis.location?.href);
			url.searchParams.set("locale", locale);
			const response = await this.#options.fetchImpl(url.toString(), {
				method: "POST",
				body: audio,
				headers: { "content-type": audio.type || "audio/webm" }
			});
			if (!response.ok) throw new Error(`Speech service returned ${response.status}`);
			const parsed = CloudResponse.safeParse(await response.json());
			if (!parsed.success || this.#discarded) return;
			if (parsed.data.transcript.length === 0) return;
			const chunk = {
				transcript: parsed.data.transcript,
				isFinal: parsed.data.isFinal,
				...parsed.data.confidence === void 0 ? {} : { confidence: parsed.data.confidence }
			};
			this.#handlers.onChunk(chunk);
		} catch (error) {
			if (this.#discarded) return;
			this.#handlers.onError({
				code: "network",
				message: error instanceof Error ? error.message : "The speech service was unreachable"
			});
			this.stop();
		}
	}
	stop() {
		this.#recording = false;
		this.#clearSegmentTimer();
		const recorder = this.#recorder;
		if (recorder && recorder.state !== "inactive") {
			recorder.stop();
			return;
		}
		this.#finish();
	}
	abort() {
		this.#discarded = true;
		this.#teardown();
	}
	/** Drop the recorder without letting its handlers fire. */
	#teardown() {
		this.#recording = false;
		this.#clearSegmentTimer();
		const recorder = this.#recorder;
		this.#recorder = null;
		if (recorder) {
			recorder.ondataavailable = null;
			recorder.onstop = null;
			if (recorder.state !== "inactive") recorder.stop();
		}
		this.#release();
	}
	#finish() {
		this.#release();
		if (this.#ended || this.#discarded) return;
		this.#ended = true;
		this.#handlers.onEnd();
	}
	#release() {
		this.#recorder = null;
		this.#recording = false;
		this.#clearSegmentTimer();
		releaseStream(this.#stream);
		this.#stream = null;
	}
};
/** Stop every track, so the browser's recording indicator actually goes out. */
function releaseStream(stream) {
	for (const track of stream?.getTracks() ?? []) track.stop();
}
function isPermissionError(error) {
	return error instanceof Error && /notallowed|permission|denied/i.test(error.name + error.message);
}
function createCloudSpeechEngine(options = {}) {
	const endpoint = options.endpoint?.trim() ?? "";
	return {
		id: "cloud",
		label: "Burfy speech service",
		audioDestination: "the Burfy speech service",
		isAvailable: () => endpoint.length > 0 && typeof MediaRecorder !== "undefined" && (options.getUserMedia !== void 0 || typeof navigator !== "undefined" && typeof navigator.mediaDevices?.getUserMedia === "function"),
		create: (handlers) => new CloudRecognizer({
			endpoint,
			chunkMs: options.chunkMs ?? 3e3,
			fetchImpl: options.fetchImpl ?? ((...args) => fetch(...args)),
			getUserMedia: options.getUserMedia ?? defaultGetUserMedia,
			recorderFactory: options.recorderFactory ?? ((stream) => new MediaRecorder(stream))
		}, handlers)
	};
}
//#endregion
export { CandidatePopup, Dictation, DocsEditor, EditorMenuBar, FindReplacePanel, LanguagePicker, PAGE_GAP_PT, PageChrome, Pagination, ParagraphStyle, Search, Toolbar, TranslitSource, VoiceButton, createCloudSpeechEngine, createEditorMenus, createEmptyDocument, createExtensions, createWebSpeechEngine, interimText, normaliseLinkHref, pageStackHeightPx, readToolbarState, sanitizePastedHtml, sanitizeStyleAttribute, speechErrorMessage, toDocumentNode, toEditorContent, unavailableMessage, useDictation, usePagination, useSearch, useTransliteration };
